import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { MultiSelect } from "primereact/multiselect";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { v4 as uuidv4 } from "uuid";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { RouterAddress } from "../../../constants/RouterAddress";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, JOB_STATUS, USER_GROUP_ADMIN } from "../../../constants/constants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import {
    dashboardPageContainerStyle,
    renderDashboardHeader,
} from "../../dashboard/pages/dashboardStyles";
import {
    appAccentPanelStyle,
    appConfirmDialogClasses,
    appDialogContentStyle,
    appDialogHeaderStyle,
    appDialogStyle,
    appFormSectionStyle,
    appMutedTextStyle,
    appNeutralPanelStyle,
    appSectionCardStyle,
    appWideDialogStyle,
    renderConfirmMessage,
    renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import Step01ResidentInfo, {
    DEFAULT_STEP01,
    Step01FormValues,
} from "../nurseAssessmentForm/steps/Step01ResidentInfo";
import Step02EmergencyEvacuation, {
    DEFAULT_STEP02,
    Step02FormValues,
} from "../nurseAssessmentForm/steps/Step02EmergencyEvacuation";
import Step03DementiaScreening, {
    DEFAULT_STEP03,
    Step03FormValues,
} from "../nurseAssessmentForm/steps/Step03DementiaScreening";
import Step04CurrentMedicalDiagnosis, {
    DEFAULT_STEP04,
    Step04FormValues,
} from "../nurseAssessmentForm/steps/Step04CurrentMedicalDiagnosis";
import Step05CurrentlyPrescribedMedications, {
    DEFAULT_STEP05,
    Step05FormValues,
} from "../nurseAssessmentForm/steps/Step05CurrentlyPrescribedMedications";
import Step06PsychosocialProfile, {
    DEFAULT_STEP06,
    Step06FormValues,
} from "../nurseAssessmentForm/steps/Step06PsychosocialProfile";
import Step07MentalHealthScreening, {
    DEFAULT_STEP07,
    Step07FormValues,
} from "../nurseAssessmentForm/steps/Step07MentalHealthScreening";
import Step08CognitiveAssessment, {
    DEFAULT_STEP08,
    Step08FormValues,
} from "../nurseAssessmentForm/steps/Step08CognitiveAssessment";
import Step09SensoryCommunication, {
    DEFAULT_STEP09,
    Step09FormValues,
} from "../nurseAssessmentForm/steps/Step09SensoryCommunication";
import Step10MedicalSystemsReview, {
    DEFAULT_STEP10,
    Step10FormValues,
} from "../nurseAssessmentForm/steps/Step10MedicalSystemsReview";
import StepCarePlanIntro from "../nurseAssessmentForm/steps/StepCarePlanIntro";
import Step11ActivitiesOfDailyLiving, {
    DEFAULT_STEP11,
    Step11FormValues,
} from "../nurseAssessmentForm/steps/Step11ActivitiesOfDailyLiving";
import BodyChartSection from "../careplan/bodyChartSection";
import Step12BehavioralObservations, {
    DEFAULT_STEP12,
    Step12FormValues,
} from "../nurseAssessmentForm/steps/Step12BehavioralObservations";
import Step13Signatures, {
    DEFAULT_STEP13,
    Step13FormValues,
} from "../nurseAssessmentForm/steps/Step13Signatures";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { BODY_CHART_CARE_PLAN_TYPE } from "../../../constants/carePlanBodyChart";
import { DEFAULT_BODY_CHART_SECTION } from "../../../constants/carePlanConstants";
import NurseFormStepperBar from "../nurseAssessmentForm/NurseFormStepperBar";
import {
    NURSE_FORM_ASSESSMENT_STEPS,
    NURSE_FORM_CARE_PLAN_STEPS,
} from "../nurseAssessmentForm/nurseAssessmentFormConstants";
import {
    ensureResidentBinderBundle,
    getBinderFolderByCode,
    RESIDENT_BINDER_FOLDER_CODES,
    upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";

const ASSESSMENT_SECTION_STEPS = NURSE_FORM_ASSESSMENT_STEPS;
const CARE_PLAN_SECTION_STEPS = NURSE_FORM_CARE_PLAN_STEPS;

const NURSE_SECTION_LABELS: Record<string, string> = {
    "STEP#01": "Resident Info & Demographics",
    "STEP#02": "Emergency Evacuation",
    "STEP#03": "Dementia Screening",
    "STEP#04": "Current Medical Diagnosis",
    "STEP#05": "Currently Prescribed Medications",
    "STEP#06": "Psychosocial Profile",
    "STEP#07": "Mental Health Screening",
    "STEP#08": "Cognitive Assessment",
    "STEP#09": "Sensory & Communication",
    "STEP#10": "Medical Systems Review",
    "STEP#11": "Activities of Daily Living",
    "STEP#12": "Body Chart",
    "STEP#13": "Behavioral Observations",
    "STEP#14": "Signatures",
};

const getSectionDisplayName = (section: any): string => {
    if (!section) return "Preparing assessment";
    return section.description || NURSE_SECTION_LABELS[String(section.recordType || "")] || String(section.recordType || "Preparing assessment");
};

const renderAccordionHeader = (index: number, label: string, icon: string, activeStep: number | null) => {
    const isCarePlanPage = label === "Care Plan Page";
    const isCompletedStep = index < (activeStep ?? -1);
    const isActiveStep = index === activeStep;

    return (
        <span
            style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                fontWeight: 700,
                fontSize: "0.92rem",
                color: isCarePlanPage ? "#9a3412" : "#0f172a",
                background: isCarePlanPage ? "#fff7ed" : "transparent",
                border: isCarePlanPage ? "1px solid #fed7aa" : "1px solid transparent",
                borderRadius: 999,
                padding: isCarePlanPage ? "0.2rem 0.55rem 0.2rem 0.2rem" : 0,
            }}
        >
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: isCompletedStep ? "#f97316" : isActiveStep ? "#1d4ed8" : (isCarePlanPage ? "#fdba74" : "#94a3b8"),
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    flexShrink: 0,
                }}
            >
                {isCompletedStep
                    ? <i className="pi pi-check" style={{ fontSize: "0.58rem" }} />
                    : String(index + 1).padStart(2, "0")}
            </span>
            <i className={icon} style={{ color: isActiveStep ? "#1d4ed8" : (isCarePlanPage ? "#c2410c" : "#64748b"), marginLeft: 2, fontSize: "0.9rem" }} />
            {label}
        </span>
    );
};

const nursePageStyles = {
    pageShell: {
        padding: "1.5rem",
        background: "linear-gradient(180deg, #f8fbff 0%, #f7f7f5 38%, #ffffff 100%)",
        minHeight: "100vh",
    } as const,
    contentShell: {
        ...dashboardPageContainerStyle,
        display: "flex",
        flexDirection: "column" as const,
        gap: "1rem",
    },
    heroCard: {
        ...appSectionCardStyle,
        padding: "1.5rem",
        borderRadius: 22,
        border: "1px solid #dbe7f3",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 52%, #eef6ff 100%)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    },
    heroGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.8fr) minmax(280px, 1fr)",
        gap: "1rem",
        alignItems: "stretch",
    },
    heroHeading: {
        fontSize: "1.7rem",
        fontWeight: 800,
        letterSpacing: "-0.03em",
        color: "#0f172a",
        marginBottom: "0.5rem",
    },
    heroLead: {
        color: "#475569",
        fontSize: "0.98rem",
        lineHeight: 1.65,
        maxWidth: "56rem",
    },
    uploadWorkspace: {
        display: "grid",
        gridTemplateColumns: "minmax(190px, 240px) minmax(0, 1fr)",
        gap: "1rem",
        alignItems: "start",
    },
    guideRail: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "0.85rem",
    },
    guideStepCard: {
        borderRadius: 18,
        padding: "0.9rem 0.95rem",
        border: "1px solid #dbe7f3",
        background: "rgba(255,255,255,0.92)",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
    },
    guideStepIndex: {
        fontSize: "0.72rem",
        fontWeight: 800,
        textTransform: "uppercase" as const,
        letterSpacing: "0.08em",
        color: "#64748b",
        marginBottom: "0.45rem",
    },
    guideStepTitle: {
        fontSize: "0.92rem",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "0.15rem",
    },
    guideStepText: {
        fontSize: "0.8rem",
        lineHeight: 1.45,
        color: "#475569",
    },
    heroPanel: {
        borderRadius: 18,
        padding: "1rem",
        background: "rgba(255,255,255,0.88)",
        border: "1px solid #dbe7f3",
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "space-between",
        gap: "0.9rem",
    },
    heroPanelTitle: {
        fontSize: "0.78rem",
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
        color: "#64748b",
    },
    heroPanelValue: {
        fontSize: "1rem",
        fontWeight: 800,
        color: "#0f172a",
    },
    sectionHint: {
        marginTop: "0.8rem",
        padding: "0.85rem 1rem",
        borderRadius: 16,
        background: "#ffffff",
        border: "1px solid #dbe7f3",
        color: "#334155",
        fontSize: "0.86rem",
        lineHeight: 1.55,
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
    },
    statCard: {
        borderRadius: 18,
        padding: "1rem 1.05rem",
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
    },
    statLabel: {
        fontSize: "0.76rem",
        fontWeight: 800,
        textTransform: "uppercase" as const,
        letterSpacing: "0.08em",
        color: "#64748b",
        marginBottom: "0.45rem",
    },
    statHint: {
        marginTop: "0.4rem",
        color: "#64748b",
        fontSize: "0.82rem",
    },
    workspaceGrid: {
        display: "grid",
        gap: "1rem",
        alignItems: "start",
        marginBottom: "1rem",
    },
    uploadCard: {
        ...appSectionCardStyle,
        borderRadius: 22,
        border: "1px solid #dbe7f3",
        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
    },
    uploadHeader: {
        padding: "1.15rem 1.2rem",
        background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
        borderBottom: "1px solid #dbe7f3",
    },
    uploadTitleRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "0.45rem",
        fontSize: "1rem",
    },
    uploadBody: {
        padding: "1.2rem",
        display: "flex",
        flexDirection: "column" as const,
        gap: "1rem",
    },
    infoCard: {
        borderRadius: 18,
        border: "1px solid #dbe7f3",
        background: "#ffffff",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
        padding: "1rem",
    },
    rowCard: {
        borderRadius: 18,
        border: "1px solid #dbe7f3",
        background: "#ffffff",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
        padding: "1rem",
    },
    rowCardHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "0.9rem",
        flexWrap: "wrap" as const,
    },
    rowCardMeta: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "0.6rem 1rem",
        marginTop: "0.8rem",
    },
    metaLabel: {
        fontSize: "0.72rem",
        fontWeight: 800,
        textTransform: "uppercase" as const,
        letterSpacing: "0.08em",
        color: "#64748b",
        marginBottom: "0.2rem",
    },
    metaValue: {
        color: "#0f172a",
        fontSize: "0.86rem",
        lineHeight: 1.5,
        wordBreak: "break-word" as const,
    },
    actionCard: {
        ...appSectionCardStyle,
        borderRadius: 22,
        border: "1px solid #dbe7f3",
        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
    },
    actionHeader: {
        padding: "1rem 1.15rem",
        borderBottom: "1px solid #e2e8f0",
        background: "#ffffff",
    },
    stickyNav: {
        position: "sticky" as const,
        top: 0,
        zIndex: 100,
        background: "rgba(255,255,255,0.94)",
        border: "1px solid #dbe7f3",
        borderRadius: 16,
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.07)",
        padding: "0.55rem 0.65rem",
        marginBottom: "0.7rem",
        backdropFilter: "blur(12px)",
    },
    formCard: {
        ...appSectionCardStyle,
        borderRadius: 24,
        border: "1px solid #dbe7f3",
        boxShadow: "0 20px 40px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
    },
    formHeader: {
        padding: "1.2rem 1.25rem",
        borderBottom: "1px solid #e2e8f0",
        background: "linear-gradient(135deg, #fffaf0 0%, #ffffff 55%, #f8fafc 100%)",
    },
    formBody: {
        padding: "1rem",
    },
} as const;

const dialogMetricLabelStyle = {
    fontSize: "0.78rem",
    fontWeight: 800,
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
};

const getStatusChipStyle = (tone: "success" | "warning" | "neutral") => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    padding: "0.25rem 0.65rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    background: tone === "success" ? "#dcfce7" : tone === "warning" ? "#fff7ed" : "#f8fafc",
    color: tone === "success" ? "#166534" : tone === "warning" ? "#9a3412" : "#334155",
    border: tone === "success" ? "1px solid #86efac" : tone === "warning" ? "1px solid #fed7aa" : "1px solid #cbd5e1",
});

const startDraftDialogStyle = {
    ...appDialogStyle,
    width: "min(96vw, 58rem)",
};

const startDraftDialogContentStyle = {
    ...appDialogContentStyle,
    maxHeight: "80vh",
    overflowY: "auto" as const,
};

const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt", "png", "jpg", "jpeg", "tif", "tiff", "mp3", "wav", "m4a"];
const ALLOWED_ACCEPT = ".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff,.mp3,.wav,.m4a,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,image/tiff,audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/m4a";
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "tif", "tiff"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "m4a"]);
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_IMAGE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_STAGED_FILES = 10;

const DOC_TAG_OPTIONS = [
    { label: "Face Sheet", value: "FaceSheet" },
    { label: "MAR Sheet", value: "MARSheet" },
    { label: "Assessment Notes", value: "AssessmentNotes" },
    // { label: "Other", value: "Other" },
];

const TAG_LABELS: Record<string, string> = {
    FaceSheet: "Face Sheet",
    MARSheet: "MAR Sheet",
    AssessmentNotes: "Assessment Notes",
    // Other: "Other",
};

const getDocumentTags = (doc: any): string[] => {
    const rawTags = Array.isArray(doc?.tags)
        ? doc.tags
        : doc?.tag
            ? [doc.tag]
            : [];

    return Array.from(
        new Set(
            rawTags
                .map((tag: any) => String(tag || "").trim())
                .filter(Boolean)
        )
    );
};

// Each item in the staging queue
interface StagedFile {
    id: string;          // temp local ID
    file: File;
    tags: string[];
}

type NurseResidentAssessmentPageProps = {
    embedded?: boolean;
};

export const NurseResidentAssessmentPage = ({ embedded = false }: NurseResidentAssessmentPageProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { residentId, homeId } = useParams<{ residentId: string; homeId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { authStore, backendAPIStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const settledBatchStatuses = useRef<Record<string, string>>({});
    const loadAssessmentRef = useRef<(targetFormId?: string) => Promise<void>>(async () => { });
    const deferredAssessmentLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSavedAssessmentDocumentJobIdsRef = useRef<Set<string>>(new Set());
    const autoSaveAssessmentDocumentForCompletedJobRef = useRef<(jobDetails: any) => Promise<void>>(async () => { });
    const accordionRef = useRef<HTMLDivElement>(null);

    const [documents, setDocuments] = useState<any[]>([]);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [startDraftDialogError, setStartDraftDialogError] = useState<{ summary: string; detail: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [formId, setFormId] = useState<string>("");
    const [assessmentHeaders, setAssessmentHeaders] = useState<any[]>([]);
    const [currentFormHeader, setCurrentFormHeader] = useState<any | null>(null);
    const [loadingForm, setLoadingForm] = useState(true);
    const [savingForm, setSavingForm] = useState(false);
    const [step01Data, setStep01Data] = useState<Partial<Step01FormValues> | null>(null);
    const [step02Data, setStep02Data] = useState<Partial<Step02FormValues> | null>(null);
    const [step03Data, setStep03Data] = useState<Partial<Step03FormValues> | null>(null);
    const [step04Data, setStep04Data] = useState<Partial<Step04FormValues> | null>(null);
    const [step05Data, setStep05Data] = useState<Partial<Step05FormValues> | null>(null);
    const [step06Data, setStep06Data] = useState<Partial<Step06FormValues> | null>(null);
    const [step07Data, setStep07Data] = useState<Partial<Step07FormValues> | null>(null);
    const [step08Data, setStep08Data] = useState<Partial<Step08FormValues> | null>(null);
    const [step09Data, setStep09Data] = useState<Partial<Step09FormValues> | null>(null);
    const [step10Data, setStep10Data] = useState<Partial<Step10FormValues> | null>(null);
    const [step11Data, setStep11Data] = useState<Partial<Step11FormValues> | null>(null);
    const [step12Data, setStep12Data] = useState<any | null>(null);
    const [step13Data, setStep13Data] = useState<Partial<Step12FormValues> | null>(null);
    const [step14Data, setStep14Data] = useState<Partial<Step13FormValues> | null>(null);
    const [activeStep, setActiveStep] = useState<number | null>(null);
    const [showDocViewer, setShowDocViewer] = useState(false);
    const [docViewerNonImageUrl, setDocViewerNonImageUrl] = useState("");
    const [docViewerImageUrl, setDocViewerImageUrl] = useState("");
    const [showAssessmentDocsDialog, setShowAssessmentDocsDialog] = useState(false);
    const [showStartDraftingDialog, setShowStartDraftingDialog] = useState(false);
    const [showAssessmentHistoryDialog, setShowAssessmentHistoryDialog] = useState(false);
    const [automationWizardStep, setAutomationWizardStep] = useState<"choose" | "files">("choose");
    const [automationShouldCreateNewVersion, setAutomationShouldCreateNewVersion] = useState(false);
    const [showProcessingDialog, setShowProcessingDialog] = useState(false);
    const [processingJobDetails, setProcessingJobDetails] = useState<any>(null);
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState<string>("");
    const [featureAccessDialogAvailableCredits, setFeatureAccessDialogAvailableCredits] = useState<number>(0);
    const [featureAccessDialogAllowUseCredit, setFeatureAccessDialogAllowUseCredit] = useState<boolean>(false);
    const [featureAccessDialogUseCreditAction, setFeatureAccessDialogUseCreditAction] = useState<(() => void) | null>(null);
    const [downloadingAssessment, setDownloadingAssessment] = useState(false);
    const [downloadingMarSheet, setDownloadingMarSheet] = useState(false);
    const [creatingManualVersion, setCreatingManualVersion] = useState(false);
    const [tagValidationRequested, setTagValidationRequested] = useState(false);
    const [autoSaveIndicator, setAutoSaveIndicator] = useState<{
        visible: boolean;
        message: string;
        state: "saving" | "saved";
    }>({ visible: false, message: "", state: "saved" });
    const [forceShowUploadWorkspace, setForceShowUploadWorkspace] = useState(false);
    const [providerCanUseNurseWorkspace, setProviderCanUseNurseWorkspace] = useState(false);
    const [residentProfile, setResidentProfile] = useState<any | null>(null);
    const [currentUserProfile, setCurrentUserProfile] = useState<any | null>(null);

    const userRoleType = authStore.getUserRoleType();
    const userGroups = authStore.getUserGroups() || [];
    const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/assessment/");
    const isProviderResidentMode = (
        (location.pathname.startsWith("/home/") && location.pathname.includes("/assessment/"))
        || (location.pathname.startsWith("/provider/nurse/resident/") && location.pathname.includes("/assessment/"))
    );
    const isProviderNurseMode = location.pathname.startsWith("/provider/nurse/resident/") && location.pathname.includes("/assessment/");
    const usesProviderAssessmentApi = isProviderResidentMode || isSharedResidentMode;
    const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;
    const sharedDashboardPath = userRoleType === ROLE_TYPE_MHP
        ? RouterAddress.MHP_HOME
        : isProviderNurseMode
            ? RouterAddress.PROVIDER_NURSE_HOME
            : RouterAddress.NURSE_HOME;
    const isStandaloneNurseWorkspaceMode = !embedded && (!isProviderResidentMode || isProviderNurseMode);
    const canShowProviderNurseDocumentActions = userRoleType === ROLE_TYPE_PROVIDER
        && providerCanUseNurseWorkspace
        && isProviderResidentMode;
    const generateDelegationDrafts = isProviderNurseMode;
    const mobile = useResponsive().isMobile;
    const profilePagePath = isSharedResidentMode
        ? RouterAddress.getSharedResidentProfilePagePath(homeId || "", residentId || "")
        : isProviderNurseMode
        ? RouterAddress.getProviderNurseResidentProfilePagePath(residentId || "")
        : isProviderResidentMode
            ? RouterAddress.getProviderResidentProfilePagePath(homeId || "", residentId || "")
            : RouterAddress.getNurseResidentProfilePagePath(residentId || "");
    const assessmentPagePath = isSharedResidentMode
        ? RouterAddress.getSharedResidentAssessmentPagePath(homeId || "", residentId || "")
        : isProviderNurseMode
        ? RouterAddress.getProviderNurseResidentAssessmentPagePath(residentId || "")
        : isProviderResidentMode
            ? RouterAddress.getProviderResidentAssessmentPagePath(homeId || "", residentId || "")
            : RouterAddress.getNurseResidentAssessmentPagePath(residentId || "");
    const delegationPagePath = isSharedResidentMode
        ? RouterAddress.getSharedResidentDelegationPagePath(homeId || "", residentId || "")
        : isProviderNurseMode
        ? RouterAddress.getProviderNurseResidentDelegationPagePath(residentId || "")
        : isProviderResidentMode
            ? RouterAddress.getProviderResidentDelegationPagePath(homeId || "", residentId || "")
            : RouterAddress.getNurseResidentDelegationPagePath(residentId || "");
    const binderPagePath = isSharedResidentMode
        ? RouterAddress.getSharedResidentBinderPagePath(homeId || "", residentId || "")
        : isProviderNurseMode
        ? RouterAddress.getProviderNurseResidentBinderPagePath(residentId || "")
        : isProviderResidentMode
            ? RouterAddress.getResidentBinderPagePath(homeId || "", residentId || "")
            : RouterAddress.getNurseResidentBinderPagePath(residentId || "");
    const hasAssessmentHistory = assessmentHeaders.some((assessment: any) => assessment.formId !== formId);
    const currentAssessmentHeader = useMemo(() => {
        if (currentFormHeader?.formId === formId) {
            return currentFormHeader;
        }
        return assessmentHeaders.find((assessment: any) => assessment.formId === formId) || null;
    }, [assessmentHeaders, currentFormHeader, formId]);
    const currentAssessmentStatus = String(currentAssessmentHeader?.status || "draft").toLowerCase();
    const isAssessmentCompleted = currentAssessmentStatus === "completed";
    const getLosAngelesDateInputValue = useCallback(() => {
        const formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/Los_Angeles",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
        return formatter.format(new Date());
    }, []);
    const currentUserDisplayName = useMemo(() => {
        const profileName = `${String(currentUserProfile?.firstName || "").trim()} ${String(currentUserProfile?.lastName || "").trim()}`.trim();
        return profileName
            || String(currentUserProfile?.name || "").trim()
            || String(authStore.getUserName() || "").trim()
            || String(authStore.getUserEmailId() || "").trim();
    }, [authStore, currentUserProfile]);
    const currentUserPhoneNumber = useMemo(() =>
        String(
            currentUserProfile?.phoneNumber
            || currentUserProfile?.phone
            || currentUserProfile?.userDetail?.phoneNumber
            || "",
        ).trim(),
    [currentUserProfile]);
    const residentDisplayName = useMemo(() => {
        const profileName = `${String(residentProfile?.firstName || "").trim()} ${String(residentProfile?.lastName || "").trim()}`.trim();
        return profileName
            || String((step01Data as any)?.residentName || "").trim();
    }, [residentProfile, step01Data]);
    const residentPhoneNumber = useMemo(() =>
        String(
            residentProfile?.phoneNumber
            || residentProfile?.primaryPhoneNumber
            || "",
        ).trim(),
    [residentProfile]);
    const step14AutofillData = useMemo<Partial<Step13FormValues>>(() => {
        const today = getLosAngelesDateInputValue();
        return {
            assessor1Name: currentUserDisplayName,
            assessor1Date: today,
            assessor1Phone: currentUserPhoneNumber,
            assessor2Name: residentDisplayName,
            assessor2Date: today,
            assessor2Phone: residentPhoneNumber,
            assessor3Name: "",
            assessor3Date: "",
            assessor3Phone: "",
        };
    }, [currentUserDisplayName, currentUserPhoneNumber, getLosAngelesDateInputValue, residentDisplayName, residentPhoneNumber]);
    const hasMissingDocumentTags = stagedFiles.some((file) => !file.tags?.length);
    const requestedFormId = searchParams.get("formId") || "";
    const handleOpenAssessmentVersion = useCallback((targetFormId: string) => {
        if (!targetFormId) return;
        setShowAssessmentHistoryDialog(false);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("formId", targetFormId);
        nextParams.delete("step");
        setSearchParams(nextParams, { replace: false });
        setActiveStep(null);
    }, [searchParams, setSearchParams]);
    const requestedStepParam = searchParams.get("step") || "";
    const shouldOpenProcessingFromQuery = searchParams.get("showProcessing") === "1";
    const requestedProcessingJobId = String(searchParams.get("jobId") || "").trim();

    useEffect(() => {
        let isMounted = true;

        const loadProviderWorkspaceCapability = async () => {
            if (isMounted) {
                setProviderCanUseNurseWorkspace(userRoleType === ROLE_TYPE_PROVIDER);
            }
        };

        void loadProviderWorkspaceCapability();

        return () => {
            isMounted = false;
        };
    }, [authStore, backendAPIStore, userRoleType]);

    useEffect(() => {
        let cancelled = false;

        const loadSignatureAutofillProfiles = async () => {
            if (!residentId) {
                return;
            }

            try {
                await backendAPIStore.init();
                const [userContext, profile] = await Promise.all([
                    authStore.loadUserContext(),
                    (isProviderResidentMode || isSharedResidentMode)
                        ? backendAPIStore.getProviderResidentProfile(residentId)
                        : backendAPIStore.getNurseResidentProfile(residentId),
                ]);

                if (cancelled) {
                    return;
                }

                setCurrentUserProfile(userContext?.profile || null);
                setResidentProfile(profile || null);
            } catch {
                if (!cancelled) {
                    setCurrentUserProfile((prev) => prev || null);
                    setResidentProfile((prev) => prev || null);
                }
            }
        };

        void loadSignatureAutofillProfiles();

        return () => {
            cancelled = true;
        };
    }, [authStore, backendAPIStore, isProviderResidentMode, isSharedResidentMode, residentId]);

    const handleAddMoreDocuments = () => {
        setForceShowUploadWorkspace(true);
        setTagValidationRequested(false);
        if (typeof window !== "undefined") {
            window.setTimeout(() => fileInputRef.current?.click(), 60);
        }
    };

    const openNurseFeatureAccessDialog = useCallback(() => {
        setFeatureAccessDialogMessage(
            "You have already used your free nurse assessment execution. To continue, review the monthly nurse plans in Billing."
        );
        setShowFeatureAccessDialog(true);
    }, []);

    const isSubscriptionRequiredError = useCallback((error: any) => {
        const code = String(error?.code || error?.cause?.message || error?.details?.code || "").trim().toUpperCase();
        const message = String(error?.message || error?.details?.error || "").toLowerCase();
        return code === "SUBSCRIPTION_REQUIRED"
            || code === "SUBSCRIPTION_LIMIT_REACHED"
            || message.includes("active subscription is required")
            || message.includes("review billing to continue")
            || message.includes("monthly nurse plans")
            || message.includes("nurse automation credits");
    }, []);

    const openAssessmentSubscriptionDialog = useCallback((message?: string) => {
        setFeatureAccessDialogAvailableCredits(0);
        setFeatureAccessDialogAllowUseCredit(false);
        setFeatureAccessDialogUseCreditAction(null);
        setFeatureAccessDialogMessage(
            requiresProviderSubscriptionMessageOnly
                ? "The provider must have an active subscription to use the shared resident assessment workspace."
                : (String(message || "").trim()
                    || "An active subscription is required to continue with nurse assessments. Review Billing to continue.")
        );
        setShowFeatureAccessDialog(true);
    }, [requiresProviderSubscriptionMessageOnly]);

    const openProviderNurseCreditDialog = useCallback((error?: any, options?: { allowUseCredit?: boolean; onUseCredit?: () => void; }) => {
        if (requiresProviderSubscriptionMessageOnly) {
            setFeatureAccessDialogAvailableCredits(0);
            setFeatureAccessDialogAllowUseCredit(false);
            setFeatureAccessDialogUseCreditAction(null);
            setFeatureAccessDialogMessage("The provider must have an active subscription to use the shared resident assessment workspace.");
            setShowFeatureAccessDialog(true);
            return;
        }
        const availableCredits = Number(
            error?.automationCreditsAvailable
            || error?.details?.automationCreditsAvailable
            || 0
        );
        const canUseCredit = Boolean(options?.allowUseCredit && availableCredits > 0);
        setFeatureAccessDialogAvailableCredits(availableCredits);
        setFeatureAccessDialogAllowUseCredit(canUseCredit);
        setFeatureAccessDialogUseCreditAction(() => (canUseCredit && options?.onUseCredit ? options.onUseCredit : null));
        setFeatureAccessDialogMessage(
            availableCredits > 0
                ? "Your monthly draft uses are used up. Use 1 automation credit to continue."
                : (String(error?.message || error?.details?.error || "").trim()
                    || "You do not have any nurse automation credits right now. Buy Automation Credit to continue drafting this assessment.")
        );
        setShowFeatureAccessDialog(true);
    }, [requiresProviderSubscriptionMessageOnly]);

    const getAssessmentStatusTagSeverity = useCallback((status?: string) => {
        const normalizedStatus = String(status || "draft").trim().toLowerCase();
        return normalizedStatus === "completed" ? "success" : "warning";
    }, []);

    const getAssessmentStatusLabel = useCallback((status?: string) => {
        const normalizedStatus = String(status || "draft").trim().toLowerCase();
        return normalizedStatus === "completed" ? "Completed" : "Draft";
    }, []);

    const scrollToStepHeader = useCallback((stepIndex: number | null) => {
        if (stepIndex === null) return;

        window.setTimeout(() => {
            requestAnimationFrame(() => {
                const headers = accordionRef.current?.querySelectorAll<HTMLElement>(".p-accordion-header");
                const targetHeader = headers?.[stepIndex];
                if (!targetHeader) return;

                const rect = targetHeader.getBoundingClientRect();
                const stickyHeader = document.querySelector(".nurse-assessment-sticky-nav") as HTMLElement | null;
                const stickyHeight = stickyHeader?.offsetHeight || 0;
                const desiredTop = window.scrollY + rect.top - Math.max((window.innerHeight / 2) - (rect.height / 2), stickyHeight + 24);

                window.scrollTo({ top: Math.max(0, desiredTop), behavior: "smooth" });
            });
        }, 120);
    }, []);

    const handleStickyStepClick = useCallback((stepIndex: number) => {
        setActiveStep(stepIndex);
        scrollToStepHeader(stepIndex);
    }, [scrollToStepHeader]);

    const scrollToPageTop = useCallback(() => {
        window.setTimeout(() => {
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            });
        }, 80);
    }, []);

    // Jump to step from ?step= query param once form load
    // ing completes
    useEffect(() => {
        if (loadingForm) return;
        const stepParam = searchParams.get("step");
        if (!stepParam) return;
        const stepNumber = parseInt(stepParam, 10);
        const stepIndex = stepNumber - 1;
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex <= 14) {
            setActiveStep(stepIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingForm]);

    // Sync activeStep → URL query param and scroll to the active section
    useEffect(() => {
        if (loadingForm) return;
        const nextParams = new URLSearchParams(searchParams);
        if (formId) {
            nextParams.set("formId", formId);
        } else {
            nextParams.delete("formId");
        }

        if (activeStep === null) {
            nextParams.delete("step");
            setSearchParams(nextParams, { replace: true });
            return;
        }

        nextParams.set("step", String(activeStep + 1));
        setSearchParams(nextParams, { replace: true });
        scrollToStepHeader(activeStep);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, formId, scrollToStepHeader]);

    const applyFormRecords = useCallback((records: any[] = []) => {
        const recordMap = new Map((records ?? []).map((record: any) => [record.recordType, record]));
        setCurrentFormHeader(recordMap.get("HEADER") ?? null);
        const rawStep12 = recordMap.get("STEP#12") ?? null;
        const rawStep13 = recordMap.get("STEP#13") ?? null;
        const rawStep14 = recordMap.get("STEP#14") ?? null;
        const hasStructuredBodyChartRecord = Boolean(rawStep12) && (
            String(rawStep12?.carePlanType || "").trim().toLowerCase() === BODY_CHART_CARE_PLAN_TYPE
            || Array.isArray(rawStep12?.bodyChartMarkers)
            || Object.prototype.hasOwnProperty.call(rawStep12 || {}, "bodyChartNotes")
        );

        setStep01Data(recordMap.get("STEP#01") ?? null);
        setStep02Data(recordMap.get("STEP#02") ?? null);
        setStep03Data(recordMap.get("STEP#03") ?? null);
        setStep04Data(recordMap.get("STEP#04") ?? null);
        setStep05Data(recordMap.get("STEP#05") ?? null);
        setStep06Data(recordMap.get("STEP#06") ?? null);
        setStep07Data(recordMap.get("STEP#07") ?? null);
        setStep08Data(recordMap.get("STEP#08") ?? null);
        setStep09Data(recordMap.get("STEP#09") ?? null);
        setStep10Data(recordMap.get("STEP#10") ?? null);
        setStep11Data(recordMap.get("STEP#11") ?? null);
        setStep12Data(hasStructuredBodyChartRecord ? rawStep12 : null);
        setStep13Data((hasStructuredBodyChartRecord ? rawStep13 : rawStep12) ?? null);
        setStep14Data((hasStructuredBodyChartRecord ? rawStep14 : rawStep13) ?? null);
    }, []);

    const handleMarkAssessmentComplete = useCallback(async () => {
        if (!formId || !residentId) return;

        setLoadingForm(true);
        try {
            await backendAPIStore.init();
            if (usesProviderAssessmentApi) {
                await backendAPIStore.updateProviderAssessmentForm({ formId, status: "completed" });
            } else {
                await backendAPIStore.updateNurseAssessmentForm({ formId, status: "completed" });
            }
            const refreshedForms = usesProviderAssessmentApi
                ? await backendAPIStore.getProviderAssessmentForms(residentId)
                : await backendAPIStore.getNurseAssessmentForms(residentId);
            setAssessmentHeaders(refreshedForms?.forms || refreshedForms || []);
            const refreshedRecords: any[] = usesProviderAssessmentApi
                ? await backendAPIStore.getProviderAssessmentFormSteps(formId)
                : await backendAPIStore.getNurseAssessmentFormSteps(formId);
            applyFormRecords(refreshedRecords);
            toast.current?.show({
                severity: "success",
                summary: "Assessment completed",
                detail: "This assessment has been marked as completed.",
                life: 3500,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message,
                life: 3000,
            });
        } finally {
            setLoadingForm(false);
        }
    }, [applyFormRecords, backendAPIStore, formId, residentId, usesProviderAssessmentApi]);

    const loadSelectedForm = useCallback(async (targetFormId?: string) => {
        if (!residentId) return;
        await backendAPIStore.init();

        if (targetFormId) {
            const [response, records]: [any, any[]] = await Promise.all([
                usesProviderAssessmentApi
                    ? backendAPIStore.getProviderAssessmentForms(residentId)
                    : backendAPIStore.getNurseAssessmentForms(residentId),
                usesProviderAssessmentApi
                    ? backendAPIStore.getProviderAssessmentFormSteps(targetFormId)
                    : backendAPIStore.getNurseAssessmentFormSteps(targetFormId),
            ]);
            const availableHeaders = response?.forms || response || [];
            if (availableHeaders.length === 0) {
                setAssessmentHeaders([]);
                setFormId("");
                applyFormRecords([]);
                return;
            }
            setAssessmentHeaders(availableHeaders);
            setFormId(targetFormId);
            applyFormRecords(records);
            const header = (records || []).find((record: any) => record.recordType === "HEADER");
            if (header) {
                setAssessmentHeaders((prev) => {
                    const existingIndex = prev.findIndex((assessment: any) => assessment.formId === header.formId);
                    if (existingIndex === -1) {
                        return [header, ...prev];
                    }
                    const next = [...prev];
                    next[existingIndex] = { ...next[existingIndex], ...header };
                    return next;
                });
            }
            return;
        }

        const response: any = usesProviderAssessmentApi
            ? await backendAPIStore.getProviderAssessmentForms(residentId)
            : await backendAPIStore.getNurseAssessmentForms(residentId);
        const availableHeaders = response?.forms || response || [];
        const activeFormId = response?.activeFormId || availableHeaders[0]?.formId || "";
        setAssessmentHeaders(availableHeaders);

        if (!activeFormId) {
            setFormId("");
            applyFormRecords([]);
            return;
        }

        setFormId(activeFormId);
        applyFormRecords(response?.activeRecords || []);
    }, [residentId, backendAPIStore, applyFormRecords, navigate, profilePagePath, usesProviderAssessmentApi]);

    const startBatchPolling = useCallback((batchJobId: string, documentIds: string[]) => {
        if (!batchJobId || pollingTimers.current[batchJobId] || settledBatchStatuses.current[batchJobId]) return;

        const poll = async () => {
            try {
                const jobDetails = await backendAPIStore.getJobDetails(batchJobId);
                const status: string = jobDetails?.jobStatus || JOB_STATUS.IN_PROGRESS;
                const done = status === JOB_STATUS.COMPLETED || status === JOB_STATUS.FAILED;

                setProcessingJobDetails(jobDetails);

                if (done) {
                    settledBatchStatuses.current[batchJobId] = status;
                }

                setDocuments(prev =>
                    prev.map(d =>
                        documentIds.includes(d.documentId)
                            ? { ...d, jobStatus: status }
                            : d
                    )
                );

                if (done) {
                    delete pollingTimers.current[batchJobId];
                    await loadSelectedForm(jobDetails?.formId || formId || undefined);
                    if (deferredAssessmentLoadTimeoutRef.current) {
                        clearTimeout(deferredAssessmentLoadTimeoutRef.current);
                    }
                    deferredAssessmentLoadTimeoutRef.current = setTimeout(() => {
                        void loadAssessmentRef.current(jobDetails?.formId || formId || undefined);
                    }, 0);
                    if (status === JOB_STATUS.COMPLETED) {
                        void autoSaveAssessmentDocumentForCompletedJobRef.current(jobDetails);
                    }
                } else {
                    pollingTimers.current[batchJobId] = setTimeout(() => { void poll(); }, 5000);
                }
            } catch {
                delete pollingTimers.current[batchJobId];
            }
        };

        pollingTimers.current[batchJobId] = setTimeout(() => { void poll(); }, 5000);
    }, [backendAPIStore, formId, loadSelectedForm]);

    const loadAssessment = useCallback(async (targetFormId?: string) => {
        try {
            setLoading(true);
            await backendAPIStore.init();
            const result = usesProviderAssessmentApi
                ? await backendAPIStore.getProviderResidentAssessment(residentId!, targetFormId || formId || undefined)
                : await backendAPIStore.getNurseResidentAssessment(residentId!, targetFormId || formId || undefined);
            const docs: any[] = (result?.documents || [])
                .map((doc: any) => {
                    const settledStatus = doc.jobId ? settledBatchStatuses.current[doc.jobId] : undefined;
                    if (
                        settledStatus &&
                        doc.jobStatus !== JOB_STATUS.COMPLETED &&
                        doc.jobStatus !== JOB_STATUS.FAILED
                    ) {
                        return { ...doc, jobStatus: settledStatus };
                    }
                    return doc;
                })
                .sort((a: any, b: any) =>
                    String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
                );
            setDocuments(docs);

            const pendingByBatchJob = new Map<string, string[]>();
            docs.forEach(doc => {
                if (
                    doc.jobId &&
                    !settledBatchStatuses.current[doc.jobId] &&
                    doc.jobStatus !== JOB_STATUS.COMPLETED &&
                    doc.jobStatus !== JOB_STATUS.FAILED
                ) {
                    const ids = pendingByBatchJob.get(doc.jobId) || [];
                    ids.push(doc.documentId);
                    pendingByBatchJob.set(doc.jobId, ids);
                }
            });
            for (const [batchJobId, documentIds] of Array.from(pendingByBatchJob.entries())) {
                try {
                    const latestJobDetails = await backendAPIStore.getJobDetails(batchJobId);
                    const latestStatus = String(latestJobDetails?.jobStatus || "").trim();
                    if (latestStatus === JOB_STATUS.COMPLETED || latestStatus === JOB_STATUS.FAILED) {
                        settledBatchStatuses.current[batchJobId] = latestStatus;
                        setDocuments((previousDocuments) =>
                            previousDocuments.map((documentItem) =>
                                documentIds.includes(documentItem.documentId)
                                    ? { ...documentItem, jobStatus: latestStatus, jobSubStatus: latestJobDetails?.jobSubStatus || latestStatus }
                                    : documentItem
                            )
                        );
                        continue;
                    }
                } catch {
                    // Fall back to normal polling below if the quick reconciliation lookup fails.
                }

                startBatchPolling(batchJobId, documentIds);
            }
        } catch (error: any) {
            if (usesProviderAssessmentApi && isSubscriptionRequiredError(error)) {
                setDocuments([]);
                return;
            }
            toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load assessment documents.", life: 5000 });
        } finally {
            setLoading(false);
        }
    }, [backendAPIStore, residentId, formId, startBatchPolling, usesProviderAssessmentApi, isSubscriptionRequiredError]);

    const deferAssessmentDocumentLoad = useCallback((targetFormId?: string, delay = 0) => {
        if (deferredAssessmentLoadTimeoutRef.current) {
            clearTimeout(deferredAssessmentLoadTimeoutRef.current);
        }

        deferredAssessmentLoadTimeoutRef.current = setTimeout(() => {
            void loadAssessmentRef.current(targetFormId);
        }, delay);
    }, []);

    useEffect(() => {
        loadAssessmentRef.current = loadAssessment;
    }, [loadAssessment]);

    useEffect(() => {
        if (loadingForm) return;
        deferAssessmentDocumentLoad(formId || undefined, 120);
        return () => {
            Object.values(pollingTimers.current).forEach(clearTimeout);
            if (deferredAssessmentLoadTimeoutRef.current) {
                clearTimeout(deferredAssessmentLoadTimeoutRef.current);
            }
        };
    }, [deferAssessmentDocumentLoad, formId, loadingForm]);

    const activeProcessingJobId = processingJobDetails?.jobId
        || documents.find((doc: any) =>
            doc.jobStatus === JOB_STATUS.SUBMITTED || doc.jobStatus === JOB_STATUS.IN_PROGRESS
        )?.jobId
        || null;
    const hasActiveAssessmentDraftJob = Boolean(activeProcessingJobId);

    useEffect(() => {
        if (!showProcessingDialog || !activeProcessingJobId) return;

        let isCancelled = false;
        let refreshTimer: ReturnType<typeof setTimeout> | null = null;

        const refreshJobDetails = async () => {
            try {
                const latestJobDetails = await backendAPIStore.getJobDetails(activeProcessingJobId);
                if (isCancelled || !latestJobDetails) return;

                setProcessingJobDetails(latestJobDetails);

                const latestStatus = String(latestJobDetails?.jobStatus || "").trim();
                if (latestStatus === JOB_STATUS.COMPLETED || latestStatus === JOB_STATUS.FAILED) {
                    settledBatchStatuses.current[activeProcessingJobId] = latestStatus;
                    setDocuments((previousDocuments) =>
                        previousDocuments.map((documentItem) =>
                            documentItem.jobId === activeProcessingJobId
                                ? { ...documentItem, jobStatus: latestStatus, jobSubStatus: latestJobDetails?.jobSubStatus || latestStatus }
                                : documentItem
                        )
                    );
                    if (latestStatus === JOB_STATUS.COMPLETED) {
                        void autoSaveAssessmentDocumentForCompletedJobRef.current(latestJobDetails);
                    }
                    return;
                }

                refreshTimer = setTimeout(() => { void refreshJobDetails(); }, 4000);
            } catch {
                refreshTimer = setTimeout(() => { void refreshJobDetails(); }, 5000);
            }
        };

        void refreshJobDetails();

        return () => {
            isCancelled = true;
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
        };
    }, [activeProcessingJobId, backendAPIStore, showProcessingDialog]);

    useEffect(() => {
        const initForm = async () => {
            if (!residentId) return;
            setLoadingForm(true);
            try {
                await loadSelectedForm(requestedFormId || undefined);
            } catch (error: any) {
                if (usesProviderAssessmentApi && isSubscriptionRequiredError(error)) {
                    return;
                }
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message,
                    life: 3000,
                });
            } finally {
                setLoadingForm(false);
            }
        };
        void initForm();
    }, [residentId, loadSelectedForm, usesProviderAssessmentApi, isSubscriptionRequiredError, requestedFormId]);

    useEffect(() => {
        const handleAutoSaveStatus = (event: Event) => {
            const customEvent = event as CustomEvent<{ state: "saving" | "saved"; timestamp?: string }>;
            if (autoSaveHideTimeoutRef.current) clearTimeout(autoSaveHideTimeoutRef.current);
            setAutoSaveIndicator({
                visible: true,
                message:
                    customEvent.detail.state === "saving"
                        ? "Autosaving..."
                        : `Saved${customEvent.detail.timestamp ? ` at ${customEvent.detail.timestamp}` : ""}`,
                state: customEvent.detail.state,
            });
            if (customEvent.detail.state === "saved") {
                autoSaveHideTimeoutRef.current = setTimeout(() => {
                    setAutoSaveIndicator(prev => ({ ...prev, visible: false }));
                }, 2200);
            }
        };
        window.addEventListener("nurseform-autosave-status", handleAutoSaveStatus);
        return () => {
            window.removeEventListener("nurseform-autosave-status", handleAutoSaveStatus);
            if (autoSaveHideTimeoutRef.current) clearTimeout(autoSaveHideTimeoutRef.current);
            if (deferredAssessmentLoadTimeoutRef.current) clearTimeout(deferredAssessmentLoadTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (!shouldOpenProcessingFromQuery || !residentId) {
            return;
        }

        let cancelled = false;

        const clearProcessingIntent = () => {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("showProcessing");
            nextParams.delete("jobId");
            setSearchParams(nextParams, { replace: true });
        };

        const openFromIntent = async () => {
            try {
                if (requestedProcessingJobId) {
                    await backendAPIStore.init();
                    const jobDetails = await backendAPIStore.getJobDetails(requestedProcessingJobId);
                    if (!cancelled && jobDetails) {
                        setProcessingJobDetails(jobDetails);
                    }
                }
            } catch {
                // Best effort only; still open the dialog so users can see any available progress.
            } finally {
                if (cancelled) {
                    return;
                }
                setShowProcessingDialog(true);
                clearProcessingIntent();
            }
        };

        void openFromIntent();

        return () => {
            cancelled = true;
        };
    }, [
        backendAPIStore,
        requestedProcessingJobId,
        residentId,
        searchParams,
        setSearchParams,
        shouldOpenProcessingFromQuery,
    ]);

    const autoSaveAssessmentDocumentForCompletedJob = useCallback(async (jobDetails: any) => {
        const completedJobId = String(jobDetails?.jobId || "").trim();
        const completedFormId = String(jobDetails?.formId || formId || "").trim();
        if (!residentId || !completedJobId || !completedFormId) {
            return;
        }
        if (autoSavedAssessmentDocumentJobIdsRef.current.has(completedJobId)) {
            return;
        }

        autoSavedAssessmentDocumentJobIdsRef.current.add(completedJobId);

        try {
            await backendAPIStore.init();
            const response: any = usesProviderAssessmentApi
                ? await backendAPIStore.generateProviderAssessmentDocument(residentId, completedFormId)
                : await backendAPIStore.generateNurseAssessmentDocument(residentId, completedFormId);

            if (response?.success === false) {
                throw new Error(response?.message || "Assessment document could not be generated.");
            }

            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) {
                throw new Error(response?.message || "Assessment document could not be generated.");
            }

            const outputKey = String(response?.outputKey || "").trim();
            const fileName = String(response?.fileName || "").trim();
            if (!outputKey || !fileName) {
                throw new Error("Assessment document is missing binder save details.");
            }

            const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
            const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
            if (!bundle.binder?.binderId || !targetFolder?.folderId) {
                throw new Error("Resident assessment binder folder is not available.");
            }

            await upsertBinderDocumentWithVersion({
                backendAPIStore,
                binderId: bundle.binder.binderId,
                folderId: targetFolder.folderId,
                folderName: targetFolder.folderName || "Assessment",
                referenceId: residentId,
                title: "Resident Assessment",
                fileName,
                mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                existingDocuments: bundle.documents,
                existingObjectKey: outputKey,
            });

            toast.current?.show({
                severity: "success",
                summary: "Saved",
                detail: "The completed assessment document was saved to the resident binder automatically.",
                life: 4000,
            });
        } catch (error: any) {
            autoSavedAssessmentDocumentJobIdsRef.current.delete(completedJobId);
            if (isSubscriptionRequiredError(error)) {
                if (usesProviderAssessmentApi) {
                    openProviderNurseCreditDialog(error);
                } else {
                    openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                }
                return;
            }
            toast.current?.show({
                severity: "warn",
                summary: "Binder save needed",
                detail: error?.message || "The assessment finished, but the document could not be saved to the resident binder automatically.",
                life: 5000,
            });
        }
    }, [backendAPIStore, formId, residentId, usesProviderAssessmentApi]);

    useEffect(() => {
        autoSaveAssessmentDocumentForCompletedJobRef.current = autoSaveAssessmentDocumentForCompletedJob;
    }, [autoSaveAssessmentDocumentForCompletedJob]);

    if (userRoleType !== ROLE_TYPE_NURSE && userRoleType !== ROLE_TYPE_PROVIDER && userRoleType !== ROLE_TYPE_MHP && !isAdminUser) {
        return <Unauthorized />;
    }

    // ── Staging queue helpers ────────────────────────────────────────────────

    const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const valid: StagedFile[] = [];
        const invalid: string[] = [];

        files.forEach(file => {
            const ext = file.name.split(".").pop()?.toLowerCase() || "";
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                invalid.push(file.name);
            } else if (IMAGE_EXTENSIONS.has(ext) && file.size > MAX_IMAGE_SIZE_BYTES) {
                invalid.push(`${file.name} (too large, max 50 MB for images)`);
            } else if (AUDIO_EXTENSIONS.has(ext) && file.size > MAX_AUDIO_SIZE_BYTES) {
                invalid.push(`${file.name} (too large, max 50 MB for audio)`);
            } else if (file.size > MAX_FILE_SIZE_BYTES) {
                invalid.push(`${file.name} (too large, max 50 MB)`);
            } else {
                valid.push({ id: uuidv4(), file, tags: [] });
            }
        });

        if (invalid.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Skipped files",
                detail: `Unsupported or too large: ${invalid.join(", ")}`,
                life: 6000,
            });
        }

        setTagValidationRequested(false);
        setStagedFiles(prev => [...prev, ...valid]);
        // reset input so same file can be re-added if needed
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const updateStagedTags = (id: string, tags: string[]) => {
        setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, tags } : f));
    };

    const toggleStagedTag = (id: string, tag: string) => {
        setStagedFiles((prev) => prev.map((file) => {
            if (file.id !== id) {
                return file;
            }

            const nextTags = (file.tags || []).includes(tag)
                ? (file.tags || []).filter((existingTag) => existingTag !== tag)
                : [...(file.tags || []), tag];

            return {
                ...file,
                tags: nextTags,
            };
        }));
    };

    const removeStagedFile = (id: string) => {
        setStagedFiles(prev => prev.filter(f => f.id !== id));
    };

    // ── Upload all staged files ──────────────────────────────────────────────

    const handleUploadAll = async (useExistingCredit: boolean = false) => {
        if (stagedFiles.length === 0) return;
        if (hasActiveAssessmentDraftJob) {
            toast.current?.show({
                severity: "warn",
                summary: "Draft already running",
                detail: "An assessment draft job is already running for this resident. Wait for it to finish before starting another one.",
                life: 4000,
            });
            return;
        }

        if (hasMissingDocumentTags) {
            setTagValidationRequested(true);
            return;
        }

        setTagValidationRequested(false);
        setStartDraftDialogError(null);
        setUploading(true);
        await backendAPIStore.init();

        const uploadedDocs: Array<{ objectKey: string; documentId: string; fileName: string; tag: string; tags: string[] }> = [];
        const errors: string[] = [];

        // Phase 1: upload all files to S3 (must stay client-side)
        for (const staged of stagedFiles) {
            const documentId = uuidv4();
            const ext = staged.file.name.split(".").pop()?.toLowerCase() || "pdf";
            try {
                const { presignedUrl, objectKey } = usesProviderAssessmentApi
                    ? await backendAPIStore.getProviderAssessmentPresignedUrl(residentId!, documentId, ext)
                    : await backendAPIStore.getNurseAssessmentPresignedUrl(residentId!, documentId, ext);
                await backendAPIStore.uploadFileToS3(presignedUrl, staged.file);
                uploadedDocs.push({
                    objectKey,
                    documentId,
                    fileName: staged.file.name,
                    tag: staged.tags[0] || "AssessmentNotes",
                    tags: staged.tags,
                });
            } catch (err: any) {
                if (usesProviderAssessmentApi) {
                    setStartDraftDialogError({
                        summary: getErrorTitle(err),
                        detail: err?.message || "Unable to upload the selected source files.",
                    });
                    setUploading(false);
                    setStagedFiles([]);
                    openProviderNurseCreditDialog(err);
                    return;
                }
                errors.push(staged.file.name);
                console.error("S3 upload error for", staged.file.name, err);
            }
        }

        if (uploadedDocs.length > 0) {
            let batchJobId: string | null = null;
            let resolvedFormId = formId || undefined;
            let resolvedVersion: number | undefined = undefined;
            try {
                if (automationShouldCreateNewVersion) {
                    const createdForm = usesProviderAssessmentApi
                        ? await backendAPIStore.createProviderAssessmentForm(residentId!)
                        : await backendAPIStore.createNurseAssessmentForm(residentId!);
                    const nextFormId = String(createdForm?.formId || "").trim();
                    if (!nextFormId) {
                        throw new Error("Unable to open the assessment right now.");
                    }
                    resolvedFormId = nextFormId;
                    resolvedVersion = Number(createdForm?.version || 0) || undefined;
                }

                const result = usesProviderAssessmentApi
                    ? await backendAPIStore.createProviderResidentAssessmentJob({
                        residentId,
                        formId: resolvedFormId,
                        documents: uploadedDocs,
                        generateDelegationDrafts,
                        useExistingCredit,
                        billingAccessWorkspace: isProviderNurseMode ? "provider_nurse" : "provider_resident",
                    })
                    : await backendAPIStore.createNurseAssessmentJob({
                        residentId,
                        formId: resolvedFormId,
                        documents: uploadedDocs,
                        generateDelegationDrafts,
                        useExistingCredit,
                    });
                batchJobId = result?.jobId || null;
                resolvedFormId = result?.formId || resolvedFormId;
                resolvedVersion = result?.version || resolvedVersion;
                setProcessingJobDetails({
                    ...(result || {}),
                    jobId: result?.jobId || batchJobId,
                    formId: resolvedFormId,
                    version: resolvedVersion,
                    jobStatus: JOB_STATUS.SUBMITTED,
                    createdAt: new Date().toISOString(),
                    delegationDraftGenerationRequested: generateDelegationDrafts,
                });
                if (resolvedFormId) {
                    setFormId(resolvedFormId);
                }
            } catch (jobErr: any) {
                console.warn("Batch job submission failed:", jobErr?.message);
                setProcessingJobDetails(null);
                if (isSubscriptionRequiredError(jobErr)) {
                    setUploading(false);
                    if (usesProviderAssessmentApi) {
                        openProviderNurseCreditDialog(jobErr, {
                            allowUseCredit: !useExistingCredit,
                            onUseCredit: () => {
                                setShowFeatureAccessDialog(false);
                                void handleUploadAll(true);
                            },
                        });
                    } else {
                        openAssessmentSubscriptionDialog(jobErr?.message || jobErr?.details?.error);
                    }
                    return;
                }
                setStartDraftDialogError({
                    summary: getErrorTitle(jobErr),
                    detail: jobErr?.message || "Unable to prepare the assessment draft right now.",
                });
            }

            const queuedDocs = uploadedDocs.map((doc) => ({
                ...doc,
                formId: resolvedFormId,
                assessmentVersion: resolvedVersion,
                jobId: batchJobId,
                jobStatus: batchJobId ? JOB_STATUS.SUBMITTED : null,
                extractedOutputKey: null,
                uploadedAt: new Date().toISOString(),
                previewUrl: null,
                downloadUrl: null,
            }));

            setDocuments((prev) => {
                const docsById = new Map(prev.map((item: any) => [item.documentId, item]));
                queuedDocs.forEach((doc) => docsById.set(doc.documentId, { ...docsById.get(doc.documentId), ...doc }));
                return Array.from(docsById.values()).sort((a: any, b: any) =>
                    String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
                );
            });

            if (batchJobId) {
                startBatchPolling(batchJobId, queuedDocs.map((doc) => doc.documentId));
            }

            if (batchJobId) {
                setShowProcessingDialog(true);
                setShowStartDraftingDialog(false);
            }

            if (batchJobId) {
                toast.current?.show({
                    severity: "success",
                    summary: "Uploaded",
                    detail: `${uploadedDocs.length} document(s) uploaded and queued for processing. Open Processing Status anytime to review progress.`,
                    life: 5000,
                });
            }
        }

        setStagedFiles([]);
        setAutomationShouldCreateNewVersion(false);
        if (hasSuccessfulDocument) {
            setForceShowUploadWorkspace(false);
        }
        setUploading(false);

        if (errors.length > 0) {
            setStartDraftDialogError({
                summary: "Some uploads failed",
                detail: errors.join(", "),
            });
            toast.current?.show({
                severity: "error",
                summary: "Some uploads failed",
                detail: errors.join(", "),
                life: 6000,
            });
        }
    };

    const handleStartNewAssessment = async () => {
        setAutomationShouldCreateNewVersion(true);
        setAutomationWizardStep(stagedFiles.length > 0 ? "files" : "choose");
        setStartDraftDialogError(null);
        setShowStartDraftingDialog(true);
    };

    const handleCreateManualVersion = async () => {
        if (!residentId || creatingManualVersion) return;
        setCreatingManualVersion(true);
        try {
            await backendAPIStore.init();
            const createdForm = usesProviderAssessmentApi
                ? await backendAPIStore.createProviderAssessmentForm(residentId)
                : await backendAPIStore.createNurseAssessmentForm(residentId);
            const nextFormId = String(createdForm?.formId || "").trim();
            if (!nextFormId) {
                throw new Error("Unable to create a new assessment version right now.");
            }

            const nextParams = new URLSearchParams();
            nextParams.set("formId", nextFormId);
            navigate(`${assessmentPagePath}?${nextParams.toString()}`);
            toast.current?.show({
                severity: "success",
                summary: "New version created",
                detail: "A fresh assessment version is ready to edit.",
                life: 3000,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to create a new assessment version right now.",
                life: 4000,
            });
        } finally {
            setCreatingManualVersion(false);
        }
    };

    const handleDownloadAssessment = async () => {
        showAssessmentDocumentActionDialog();
    };

    const handleDownloadMarSheet = async (saveToBinder: boolean = false) => {
        if (!residentId) return;
        setDownloadingMarSheet(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderAssessmentApi
                ? await backendAPIStore.generateProviderAssessmentMarSheet(residentId, formId || undefined)
                : await backendAPIStore.generateNurseAssessmentMarSheet(residentId, formId || undefined);

            if (response?.success === false) {
                toast.current?.show({
                    severity: "warn",
                    summary: "MAR not ready",
                    detail: response?.message || "Save the Medications step before downloading the MAR sheet.",
                    life: 4500,
                });
                return;
            }

            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) {
                throw new Error(response?.message || "MAR sheet could not be generated.");
            }

            if (saveToBinder) {
                await saveMarSheetDocumentToBinder(response);
                toast.current?.show({
                    severity: "success",
                    summary: "Saved",
                    detail: "The MAR sheet was saved to the resident binder.",
                    life: 3500,
                });
            } else {
                openUrlInNewTab(documentUrl);
                toast.current?.show({
                    severity: "success",
                    summary: "Download started",
                    detail: "The MAR sheet is being downloaded.",
                    life: 3500,
                });
            }
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                if (usesProviderAssessmentApi) {
                    openProviderNurseCreditDialog(error);
                } else {
                    openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                }
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to download MAR sheet.",
                life: 4000,
            });
        } finally {
            setDownloadingMarSheet(false);
        }
    };

    const saveAssessmentDocumentToBinder = async (response: any) => {
        if (!residentId) {
            return;
        }

        const outputKey = String(response?.outputKey || "").trim();
        const fileName = String(response?.fileName || "").trim();
        if (!outputKey || !fileName) {
            throw new Error("Assessment document is missing binder save details.");
        }

        const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
        const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
        if (!bundle.binder?.binderId || !targetFolder?.folderId) {
            throw new Error("Resident assessment binder folder is not available.");
        }

        await upsertBinderDocumentWithVersion({
            backendAPIStore,
            binderId: bundle.binder.binderId,
            folderId: targetFolder.folderId,
            folderName: targetFolder.folderName || "Assessment",
            referenceId: residentId,
            title: "Resident Assessment",
            fileName,
            mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            existingDocuments: bundle.documents,
            existingObjectKey: outputKey,
        });
    };

    const saveMarSheetDocumentToBinder = async (response: any) => {
        if (!residentId) {
            return;
        }

        const outputKey = String(response?.outputKey || "").trim();
        const fileName = String(response?.fileName || "").trim();
        if (!outputKey || !fileName) {
            throw new Error("MAR sheet is missing binder save details.");
        }

        const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
        const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.MEDICATION_LOGS);
        if (!bundle.binder?.binderId || !targetFolder?.folderId) {
            throw new Error("Resident MAR binder folder is not available.");
        }

        await upsertBinderDocumentWithVersion({
            backendAPIStore,
            binderId: bundle.binder.binderId,
            folderId: targetFolder.folderId,
            folderName: targetFolder.folderName || "Medication Logs / MARs",
            referenceId: residentId,
            title: "Medication Administration Record",
            fileName,
            mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            existingDocuments: bundle.documents,
            existingObjectKey: outputKey,
        });
    };

    const showAssessmentDocumentActionDialog = () => {
        confirmDialog({
            message: renderConfirmMessage(
                "Assessment document",
                "Choose whether to download the assessment document or save it directly to the resident binder.",
            ),
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            rejectLabel: "Download",
            rejectClassName: "p-button-info p-button-outlined p-button-rounded",
            acceptLabel: "Add to Binder",
            acceptClassName: appConfirmDialogClasses.acceptClassName,
            accept: async () => {
                await generateAssessmentDocument(true);
            },
            reject: async () => {
                await generateAssessmentDocument(false);
            },
        });
    };

    const showMarSheetActionDialog = () => {
        confirmDialog({
            message: renderConfirmMessage(
                "MAR sheet",
                "Choose whether to download the MAR sheet or save it directly to the resident binder.",
            ),
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            rejectLabel: "Download",
            rejectClassName: "p-button-info p-button-outlined p-button-rounded",
            acceptLabel: "Add to Binder",
            acceptClassName: appConfirmDialogClasses.acceptClassName,
            accept: async () => {
                await handleDownloadMarSheet(true);
            },
            reject: async () => {
                await handleDownloadMarSheet(false);
            },
        });
    };

    const generateAssessmentDocument = async (saveToBinder: boolean) => {
        if (!residentId) return;
        setDownloadingAssessment(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderAssessmentApi
                ? await backendAPIStore.generateProviderAssessmentDocument(residentId, formId || undefined)
                : await backendAPIStore.generateNurseAssessmentDocument(residentId, formId || undefined);

            if (response?.success === false) {
                toast.current?.show({
                    severity: "warn",
                    summary: "Assessment not ready",
                    detail: response?.message || "Please save at least one assessment section before downloading.",
                    life: 4500,
                });
                return;
            }

            const documentUrl = String(response?.documentUrl || "").trim();

            if (!documentUrl) {
                throw new Error(response?.message || "Assessment document could not be generated.");
            }

            if (saveToBinder) {
                await saveAssessmentDocumentToBinder(response);
            } else {
                openUrlInNewTab(documentUrl);
            }

            toast.current?.show({
                severity: "success",
                summary: saveToBinder ? "Added to Binder" : "Download started",
                detail: saveToBinder
                    ? "The assessment document was saved to the resident binder."
                    : "The assessment Word document is being downloaded.",
                life: 3500,
            });
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                if (usesProviderAssessmentApi) {
                    openProviderNurseCreditDialog(error);
                } else {
                    openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                }
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to download assessment.",
                life: 4000,
            });
        } finally {
            setDownloadingAssessment(false);
        }
    };

    const saveStep = async (stepNumber: number, stepData: any, setter: (value: any) => void) => {
        if (!formId) return;
        setSavingForm(true);
        try {
            if (usesProviderAssessmentApi) {
                await backendAPIStore.updateProviderAssessmentForm({ formId, stepNumber, stepData });
            } else {
                await backendAPIStore.updateNurseAssessmentForm({ formId, stepNumber, stepData });
            }
            setter(stepData);
        } finally {
            setSavingForm(false);
        }
    };

    const handleSaveStep01 = async (data: Step01FormValues) => { await saveStep(1, data, setStep01Data); };
    const handleSaveStep02 = async (data: Step02FormValues) => { await saveStep(2, data, setStep02Data); };
    const handleSaveStep03 = async (data: Step03FormValues) => { await saveStep(3, data, setStep03Data); };
    const handleSaveStep04 = async (data: Step04FormValues) => { await saveStep(4, data, setStep04Data); };
    const handleSaveStep05 = async (data: Step05FormValues) => { await saveStep(5, data, setStep05Data); };
    const handleSaveStep06 = async (data: Step06FormValues) => { await saveStep(6, data, setStep06Data); };
    const handleSaveStep07 = async (data: Step07FormValues) => { await saveStep(7, data, setStep07Data); };
    const handleSaveStep08 = async (data: Step08FormValues) => { await saveStep(8, data, setStep08Data); };
    const handleSaveStep09 = async (data: Step09FormValues) => { await saveStep(9, data, setStep09Data); };
    const handleSaveStep10 = async (data: Step10FormValues) => { await saveStep(10, data, setStep10Data); };
    const handleSaveStep11 = async (data: Step11FormValues) => { await saveStep(11, data, setStep11Data); };
    const handleSaveStep12 = async (data: any) => { await saveStep(12, data, setStep12Data); };
    const handleSaveStep13 = async (data: Step12FormValues) => { await saveStep(13, data, setStep13Data); };
    const handleSaveStep14 = async (data: Step13FormValues) => { await saveStep(14, data, setStep14Data); };

    const handlePreviewDocument = (doc: any) => {
        if (!doc?.previewUrl) return;
        const fileExtension = doc.fileName?.split(".")?.pop()?.toLowerCase() || "";
        if (IMAGE_EXTENSIONS.has(fileExtension)) {
            setDocViewerImageUrl(doc.previewUrl);
            setDocViewerNonImageUrl("");
        } else {
            setDocViewerNonImageUrl(doc.previewUrl);
            setDocViewerImageUrl("");
        }
        setShowDocViewer(true);
    };

    const handleCloseProcessingDialog = () => {
        setShowProcessingDialog(false);
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                window.location.reload();
            }, 0);
        }
    };

    const processingDocuments = documents.filter((doc: any) => {
        const isProcessingState =
            doc.jobStatus === JOB_STATUS.SUBMITTED ||
            doc.jobStatus === JOB_STATUS.IN_PROGRESS ||
            (doc.jobStatus === JOB_STATUS.FAILED && doc.jobId === activeProcessingJobId);

        if (!isProcessingState) return false;
        return activeProcessingJobId ? doc.jobId === activeProcessingJobId : true;
    });
    const existingDocuments = documents.filter((doc: any) =>
        !doc.jobStatus || doc.jobStatus === JOB_STATUS.COMPLETED
    );
    const hasSuccessfulDocument = existingDocuments.length > 0;
    const queuedDocumentCount = documents.filter((doc: any) =>
        doc.jobStatus === JOB_STATUS.SUBMITTED || doc.jobStatus === JOB_STATUS.IN_PROGRESS
    ).length;
    const failedDocumentCount = documents.filter((doc: any) => doc.jobStatus === JOB_STATUS.FAILED).length;
    const nurseSections = Array.isArray(processingJobDetails?.nurseSections)
        ? processingJobDetails.nurseSections
        : [];
    const completedSections = nurseSections.filter((section: any) =>
        section?.status === "completed" || (section?.success && !section?.skipped)
    );
    const applicableSectionCount = Math.max(
        Number(processingJobDetails?.applicableSectionCount || 0),
        nurseSections.filter((section: any) => !section?.skipped).length
    );
    const isProcessingCompleted = processingJobDetails?.jobStatus === JOB_STATUS.COMPLETED;
    const isProcessingFailed = processingJobDetails?.jobStatus === JOB_STATUS.FAILED;
    const isAssessmentReadyForEditing = hasSuccessfulDocument || isProcessingCompleted;
    const currentWorkflowStep = !isAssessmentReadyForEditing ? 1 : queuedDocumentCount > 0 ? 2 : 3;
    const nextActionTitle = !isAssessmentReadyForEditing
        ? "Upload and tag assessment source files"
        : queuedDocumentCount > 0
            ? "Wait for processing to finish, then review the section results"
            : "Continue completing the nurse assessment form";
    const nextActionDescription = !isAssessmentReadyForEditing
        ? "Start with the resident's source files. Once one file finishes successfully, the assessment workspace will open automatically."
        : queuedDocumentCount > 0
            ? `${queuedDocumentCount} file${queuedDocumentCount === 1 ? "" : "s"} ${queuedDocumentCount === 1 ? "is" : "are"} still processing. You can monitor progress and prepare to review the imported sections.`
            : "Your source documents are ready. Move through the form step by step and save any updates as you go.";
    const showUploadWorkspace = forceShowUploadWorkspace || !isAssessmentReadyForEditing || stagedFiles.length > 0 || uploading;
    const isProcessingFinished = isProcessingCompleted || isProcessingFailed;
    const remainingSectionsCount = isProcessingFinished
        ? 0
        : Math.max(
            Number(processingJobDetails?.remainingSectionCount ?? (applicableSectionCount - completedSections.length)) || 0,
            0
        );
    const currentSectionName = processingJobDetails?.currentSectionDescription
        || nurseSections.find((section: any) => section?.status === "processing")?.description
        || (processingJobDetails?.jobStatus === JOB_STATUS.COMPLETED
            ? "Assessment ready"
            : processingJobDetails?.jobStatus === JOB_STATUS.FAILED
                ? "Processing finished with issues"
                : "Preparing assessment sections");
    const visibleProgressSections = completedSections.length > 0
        ? completedSections
            .slice()
            .sort((a: any, b: any) => String(a?.recordType || "").localeCompare(String(b?.recordType || "")))
        : [];
    const pendingSections = nurseSections.filter((section: any) =>
        !section?.skipped
        && section?.status !== "completed"
        && !(section?.success && !section?.skipped)
    );
    const shouldShowProcessingDialogContent = processingDocuments.length > 0 || !!processingJobDetails;
    const shouldShowUploadProgressSection = false;
    const generatedDelegationForms = Array.isArray(processingJobDetails?.generatedDelegationForms)
        ? processingJobDetails.generatedDelegationForms
        : [];
    const delegationDraftGenerationRequested = !usesProviderAssessmentApi && !!processingJobDetails?.delegationDraftGenerationRequested;
    const delegationDraftGenerationStatus = String(processingJobDetails?.delegationDraftGenerationStatus || "").trim().toLowerCase();
    const isDelegationDraftGenerationInProgress = [
        "queued",
        "submitted",
        "in_progress",
        "processing",
        "generating",
    ].includes(delegationDraftGenerationStatus);
    const formatProcessingStatus = (status?: string) => {
        const rawStatus = String(status || "").trim();
        if (!rawStatus) return "Preparing assessment";

        const normalizedStatus = rawStatus.toLowerCase();
        if (normalizedStatus.includes("parsing")) return "Reviewing documents";
        if (normalizedStatus.includes("submitted") || normalizedStatus.includes("queued")) return "Queued";
        if (normalizedStatus.includes("in progress")) return "Building assessment";
        if (normalizedStatus.includes("complete")) return "Assessment ready";
        if (normalizedStatus.includes("fail") || normalizedStatus.includes("error")) return "Needs attention";

        return rawStatus
            .replace(/[_-]+/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const renderDocumentStatus = (doc: any) => {
        if (!doc.jobStatus) {
            return <Tag value="Saved" severity="secondary" />;
        }

        if (doc.jobStatus === JOB_STATUS.SUBMITTED || doc.jobStatus === JOB_STATUS.IN_PROGRESS) {
            return (
                <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: "#eff6ff",
                    border: "1px solid #93c5fd",
                    borderRadius: 20,
                    padding: "3px 10px 3px 6px",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#1d4ed8",
                }}>
                    <ProgressSpinner
                        style={{ width: 16, height: 16 }}
                        strokeWidth="6"
                        animationDuration=".9s"
                    />
                    {formatProcessingStatus(doc.jobSubStatus || doc.jobStatus || "parsing")}
                </div>
            );
        }

        if (doc.jobStatus === JOB_STATUS.FAILED) {
            return <Tag value="Needs attention" severity="danger" />;
        }

        return <Tag value="Completed" severity="success" />;
    };

    const formatElapsedTime = (job: any) => {
        const startedAt = job?.startedAt || job?.createdAt || processingDocuments[0]?.uploadedAt;
        const finishedAt = job?.completedAt || job?.updatedAt;

        if (!startedAt) return "--";

        const startMs = new Date(startedAt).getTime();
        const endMs = finishedAt ? new Date(finishedAt).getTime() : Date.now();
        const diffSeconds = Math.max(0, Math.floor((endMs - startMs) / 1000));
        const minutes = Math.floor(diffSeconds / 60);
        const seconds = diffSeconds % 60;

        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };

    const processingSummaryItems = [
        {
            label: "Upload Documents",
            value: uploading
                ? `Uploading ${stagedFiles.length} file${stagedFiles.length === 1 ? "" : "s"}`
                : processingDocuments.length > 0
                    ? `${processingDocuments.length} uploaded`
                    : "Waiting",
        },
        { label: "Status", value: formatProcessingStatus(processingJobDetails?.jobSubStatus || processingJobDetails?.jobStatus) },
        { label: "Completed", value: applicableSectionCount > 0 ? `${completedSections.length} / ${applicableSectionCount}` : completedSections.length },
        { label: "Elapsed", value: formatElapsedTime(processingJobDetails) },
    ];

    const renderDocumentRow = (doc: any, showStatus = true) => {
        const docTags = getDocumentTags(doc);

        return (
            <div
                key={doc.documentId}
                style={{
                    ...appNeutralPanelStyle,
                    borderRadius: 18,
                    border: "1px solid #dbe7f3",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
                    background: "#ffffff",
                    padding: "1rem",
                }}
                className="flex align-items-start gap-3"
            >
                <div className="flex flex-column gap-1" style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#0f172a", wordBreak: "break-word" }}>
                        {doc.fileName}
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center mt-1">
                        {docTags.length > 0 ? docTags.map((tag) => (
                            <Tag key={`${doc.documentId}-${tag}`} value={TAG_LABELS[tag] || tag} severity="info" />
                        )) : <Tag value="Document" severity="info" />}
                        {showStatus && renderDocumentStatus(doc)}
                    </div>
                    {doc.uploadedAt && (
                        <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }} className="mt-1">
                            Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                        <Button
                            type="button"
                            label="Preview"
                            icon="pi pi-eye"
                            severity="info"
                            rounded
                            outlined
                            size="small"
                            disabled={!doc.previewUrl}
                            onClick={() => handlePreviewDocument(doc)}
                        />
                        <Button
                            type="button"
                            label="Download"
                            icon="pi pi-download"
                            severity="success"
                            rounded
                            outlined
                            size="small"
                            disabled={!doc.downloadUrl}
                            onClick={() => doc.downloadUrl && openUrlInNewTab(doc.downloadUrl)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const providerNurseResidentName = `${String((step01Data as any)?.firstName || "").trim()} ${String((step01Data as any)?.lastName || "").trim()}`.trim()
        || String((step01Data as any)?.residentFullName || "").trim()
        || "Resident";
    const renderProviderNurseAssessmentSection = () => (
        <div style={appNeutralPanelStyle} className="resident-careplan-actions">
            <div className="resident-careplan-actions-grid">
                <div className="resident-careplan-actions-info">
                    <div className="grid mb-3">
                        <div className="col-12 lg:col-8">
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>Manage Resident Assessment</div>
                            <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                Use resident source files such as Face sheet, MAR sheet, clinical records, assessment notes, and audio files to create the draft assessment and care plan.
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 align-items-center">
                                <Tag value={`VERSION ${currentAssessmentHeader?.version || 1}`} severity="info" rounded />
                                <Tag value={getAssessmentStatusLabel(currentAssessmentStatus).toUpperCase()} severity={getAssessmentStatusTagSeverity(currentAssessmentStatus)} rounded />
                                <span style={{ ...appMutedTextStyle, fontWeight: 600 }}>
                                    {currentAssessmentHeader?.modifiedAt
                                        ? `Last saved ${new Date(currentAssessmentHeader.modifiedAt).toLocaleString()}`
                                        : currentAssessmentHeader?.createdAt
                                            ? `Created ${new Date(currentAssessmentHeader.createdAt).toLocaleString()}`
                                            : "Ready to review and edit"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="resident-careplan-action-list">
                    <ul className="list-none m-0 p-0">
                        {[
                            {
                                icon: "pi pi-bolt",
                                label: "Start New Draft Automation",
                                buttonLabel: "Start",
                                severity: "warning" as const,
                                disabled: false,
                                loading: false,
                                action: handleStartNewAssessment,
                            },
                            {
                                icon: "pi pi-file-word",
                                label: "Download Assessment",
                                buttonLabel: "Download",
                                severity: "secondary" as const,
                                disabled: !formId,
                                loading: downloadingAssessment,
                                action: handleDownloadAssessment,
                            },
                            {
                                icon: "pi pi-file",
                                label: "Download MAR Sheet",
                                buttonLabel: "Download",
                                severity: "secondary" as const,
                                disabled: !formId,
                                loading: downloadingMarSheet,
                                action: showMarSheetActionDialog,
                            },
                            {
                                icon: "pi pi-check-circle",
                                label: "Mark Assessment Complete",
                                buttonLabel: "Complete",
                                severity: "secondary" as const,
                                disabled: !formId || isAssessmentCompleted,
                                loading: false,
                                action: handleMarkAssessmentComplete,
                            },
                            {
                                icon: "pi pi-history",
                                label: "Assessment History",
                                buttonLabel: "Open",
                                severity: "secondary" as const,
                                disabled: !hasAssessmentHistory,
                                loading: false,
                                action: () => setShowAssessmentHistoryDialog(true),
                            },
                        ].map((item) => (
                            <li
                                key={item.label}
                                className="flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-3 resident-careplan-action-item"
                            >
                                <span className={`flex align-items-center mr-2 gap-2${item.disabled ? " text-400" : ""}`} style={{ fontWeight: 600, color: "#0f172a" }}>
                                    <i className={`${item.icon} text-primary`} />
                                    <span>{item.label}</span>
                                </span>
                                <Button
                                    label={item.buttonLabel}
                                    severity={item.severity}
                                    rounded
                                    outlined
                                    size="small"
                                    disabled={item.disabled}
                                    loading={item.loading}
                                    onClick={item.action}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            {formId ? (
                <div className="mt-3">
                    <NurseResidentAssessmentPage embedded />
                </div>
            ) : null}
        </div>
    );

    return (
        <div
            style={isStandaloneNurseWorkspaceMode
                ? { width: "100%", maxWidth: "100%", display: "flex", flexDirection: "column", gap: "1.25rem", margin: "0 auto", padding: "1.25rem" }
                : embedded
                    ? { ...nursePageStyles.pageShell, padding: 0, background: "transparent", minHeight: "auto" }
                    : { ...nursePageStyles.pageShell, padding: mobile ? "0.75rem" : nursePageStyles.pageShell.padding }}
            className={isStandaloneNurseWorkspaceMode ? "resident-details-page m-2 p-component" : undefined}
        >
            <Toast ref={toast} />
            <div style={nursePageStyles.contentShell}>
                {isStandaloneNurseWorkspaceMode ? (
                    loading ? (
                        <div className="col-12 text-center d-flex justify-content-center align-items-center">
                            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                        </div>
                    ) : (
                        <div className="flex flex-column gap-3">
                            <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <div style={dashboardPageContainerStyle}>
                                        {renderDashboardHeader(
                                            `${providerNurseResidentName} Assessment`,
                                            isProviderNurseMode
                                                ? "Manage assessment for private client"
                                                : "Manage the resident assessment, review versions, and continue related documentation from one workspace.",
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-content-end align-items-start">
                                    <div className="flex flex-wrap justify-content-end gap-2">
                                        <Button icon="pi pi-user" outlined rounded label="Profile" severity="warning" size="small" onClick={() => navigate(profilePagePath)} />
                                        <Button icon="pi pi-folder-open" outlined rounded label="Binder" severity="warning" size="small" onClick={() => navigate(binderPagePath)} />
                                    </div>
                                </div>
                            </div>

                            {renderProviderNurseAssessmentSection()}
                        </div>
                    )
                ) : (
                    <>
                        {false && showUploadWorkspace && (
                    <div
                        style={{
                            ...nursePageStyles.workspaceGrid,
                            gridTemplateColumns: "minmax(0, 760px)",
                            width: "100%",
                        }}
                        className="align-items-center justify-content-center"
                    >
                        <div
                            style={{
                                ...nursePageStyles.uploadWorkspace,
                                gridTemplateColumns: mobile
                                    ? "minmax(0, 1fr)"
                                    : hasSuccessfulDocument
                                        ? "minmax(0, 1fr)"
                                        : "minmax(190px, 240px) minmax(0, 1fr)",
                            }}
                        >
                            {!hasSuccessfulDocument && (
                                <div style={nursePageStyles.guideRail}>
                                    {[
                                        { index: "Step 1", title: "Upload", text: "Add and tag files." },
                                        { index: "Step 2", title: "Review", text: "Wait for processing." },
                                        { index: "Step 3", title: "Complete", text: "Finish the form." },
                                    ].map((item, index) => {
                                        const isActive = currentWorkflowStep === index + 1;
                                        const isComplete = currentWorkflowStep > index + 1;
                                        return (
                                            <div
                                                key={item.index}
                                                style={{
                                                    ...nursePageStyles.guideStepCard,
                                                    border: isActive ? "1px solid #60a5fa" : isComplete ? "1px solid #86efac" : nursePageStyles.guideStepCard.border,
                                                    background: isActive ? "#eff6ff" : isComplete ? "#f0fdf4" : nursePageStyles.guideStepCard.background,
                                                }}
                                            >
                                                <div style={{ ...nursePageStyles.guideStepIndex, color: isActive ? "#1d4ed8" : isComplete ? "#15803d" : "#64748b" }}>
                                                    {item.index}
                                                </div>
                                                <div style={nursePageStyles.guideStepTitle}>{item.title}</div>
                                                <div style={nursePageStyles.guideStepText}>{item.text}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <Card style={nursePageStyles.uploadCard}>
                                <div style={nursePageStyles.uploadHeader}>
                                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={nursePageStyles.uploadTitleRow}>
                                                <i className="pi pi-upload" style={{ color: "#1d4ed8" }} />
                                                {hasSuccessfulDocument ? "Add More Documents" : "Upload Assessment Sources"}
                                            </div>
                                            <div style={{ ...appMutedTextStyle, color: "#475569", lineHeight: 1.6 }}>
                                                {hasSuccessfulDocument
                                                    ? "Add more Face Sheets, MAR Sheets, assessment notes, or audio files to enrich the current assessment and re-run processing whenever needed."
                                                    : "Upload the resident's Face Sheet, MAR Sheet, assessment notes, other clinical documents, or audio files. Tag each file with one or more categories, then generate the assessment when you are ready."}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept={ALLOWED_ACCEPT}
                                    style={{ display: "none" }}
                                    onChange={handleFilePick}
                                />

                                <div style={nursePageStyles.uploadBody}>
                                    <div>
                                        <Button
                                            label={hasSuccessfulDocument ? "Choose More Files" : "Add Files"}
                                            icon="pi pi-plus"
                                            severity="secondary"
                                            rounded
                                            outlined
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            style={{ width: mobile ? "100%" : undefined }}
                                        />
                                    </div>

                                    {stagedFiles.length > 0 && (
                                        <div className="flex flex-column gap-2">
                                            <div style={{ fontWeight: 600, color: "#0f172a" }}>
                                                Files to upload ({stagedFiles.length})
                                            </div>

                                            {uploading ? (
                                                <div style={{
                                                    background: "linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)",
                                                    border: "1px solid #93c5fd",
                                                    borderRadius: 12,
                                                    padding: "2.5rem 1.5rem",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: "1.25rem",
                                                    textAlign: "center",
                                                }}>
                                                    <ProgressSpinner
                                                        style={{ width: 56, height: 56 }}
                                                        strokeWidth="4"
                                                        animationDuration=".8s"
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: "#1d4ed8", fontSize: "1.05rem", marginBottom: "0.4rem" }}>
                                                            <i className="pi pi-sparkles mr-2" style={{ color: "#f59e0b" }} />
                                                            Uploading &amp; queuing your documents…
                                                        </div>
                                                        <div style={{ color: "#64748b", fontSize: "0.85rem" }}>
                                                            Uploading {stagedFiles.length} file{stagedFiles.length !== 1 ? "s" : ""} and submitting for processing.
                                                            Please do not close this page.
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {stagedFiles.map(staged => {
                                                        const isMissingTagSelection = tagValidationRequested && !(staged.tags?.length);
                                                        const stagedExt = staged.file.name.includes(".")
                                                            ? staged.file.name.split(".").pop()?.toLowerCase() || ""
                                                            : "";

                                                        return (
                                                            <div
                                                                key={staged.id}
                                                                style={{
                                                                    ...nursePageStyles.rowCard,
                                                                    border: isMissingTagSelection ? "1px solid #ef4444" : nursePageStyles.rowCard.border,
                                                                    background: isMissingTagSelection ? "#fef2f2" : nursePageStyles.rowCard.background,
                                                                }}
                                                            >
                                                                <div style={nursePageStyles.rowCardHeader}>
                                                                    <div style={{ display: "flex", gap: "0.85rem", alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                                                                        <div style={{
                                                                            width: 42,
                                                                            height: 42,
                                                                            borderRadius: 14,
                                                                            background: "#eff6ff",
                                                                            color: "#1d4ed8",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            flexShrink: 0,
                                                                        }}>
                                                                            <i className={AUDIO_EXTENSIONS.has(stagedExt) ? "pi pi-volume-up" : IMAGE_EXTENSIONS.has(stagedExt) ? "pi pi-image" : "pi pi-file"} />
                                                                        </div>
                                                                        <div style={{ minWidth: 0, flex: 1 }}>
                                                                            <div style={{ fontWeight: 700, color: "#0f172a", wordBreak: "break-word" }}>
                                                                                {staged.file.name}
                                                                            </div>
                                                                            <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.2rem" }}>
                                                                                {(staged.file.size / 1024 / 1024).toFixed(1)} MB{stagedExt ? ` • ${stagedExt.toUpperCase()}` : ""}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        icon="pi pi-times"
                                                                        severity="danger"
                                                                        rounded
                                                                        outlined
                                                                        size="small"
                                                                        tooltip="Remove"
                                                                        onClick={() => removeStagedFile(staged.id)}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-column gap-1" style={{ marginTop: "0.9rem" }}>
                                                                    <div style={nursePageStyles.metaLabel}>Assessment Tags</div>
                                                                    <MultiSelect
                                                                        value={staged.tags || []}
                                                                        options={DOC_TAG_OPTIONS}
                                                                        optionLabel="label"
                                                                        optionValue="value"
                                                                        onChange={e => updateStagedTags(staged.id, e.value || [])}
                                                                        placeholder="Select tag(s)"
                                                                        display="chip"
                                                                        maxSelectedLabels={3}
                                                                        className={isMissingTagSelection ? "p-invalid" : undefined}
                                                                        style={{ minWidth: mobile ? undefined : "240px", width: "100%" }}
                                                                        selectAllLabel="Select All"
                                                                    />
                                                                    {isMissingTagSelection && (
                                                                        <small style={{ color: "#dc2626", fontWeight: 600 }}>
                                                                            Select at least one tag to continue.
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}

                                                    <div className="flex gap-2 mt-1 flex-wrap" style={{ flexDirection: mobile ? "column" : "row" }}>
                                                        <Button
                                                            label="Start Drafting"
                                                            icon="pi pi-sparkles"
                                                            severity="warning"
                                                            rounded
                                                            disabled={uploading || stagedFiles.length === 0}
                                                            onClick={() => {
                                                                scrollToPageTop();
                                                                void handleUploadAll();
                                                            }}
                                                            style={{ width: mobile ? "100%" : undefined }}
                                                        />
                                                        <Button
                                                            label="Clear All"
                                                            icon="pi pi-trash"
                                                            severity="secondary"
                                                            rounded
                                                            outlined
                                                            onClick={() => {
                                                                setStagedFiles([]);
                                                                if (hasSuccessfulDocument) {
                                                                    setForceShowUploadWorkspace(false);
                                                                }
                                                            }}
                                                            style={{ width: mobile ? "100%" : undefined }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>

                    </div>
                        )}

                {/* ── Nurse Assessment Form ─────────────────────────────────── */}
                {autoSaveIndicator.visible && (
                    <div style={{
                        position: "fixed", top: mobile ? 72 : 80, right: mobile ? 12 : 24, left: mobile ? 12 : undefined, zIndex: 9999,
                        background: autoSaveIndicator.state === "saving" ? "#fef3c7" : "#d1fae5",
                        color: autoSaveIndicator.state === "saving" ? "#92400e" : "#065f46",
                        border: `1px solid ${autoSaveIndicator.state === "saving" ? "#fcd34d" : "#34d399"}`,
                        borderRadius: 8, padding: mobile ? "8px 12px" : "6px 16px", fontSize: "0.8rem", fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 6,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                    }}>
                        <i className={autoSaveIndicator.state === "saving" ? "pi pi-spin pi-spinner" : "pi pi-check"} />
                        {autoSaveIndicator.message}
                    </div>
                )}
                {/* ── Sticky section navigator ─────────────────────────── */}
                {!mobile && activeStep !== null && (
                    <div className="nurse-assessment-sticky-nav" style={{ ...nursePageStyles.stickyNav, top: mobile ? 8 : 0, padding: mobile ? "0.45rem" : nursePageStyles.stickyNav.padding }}>
                        <NurseFormStepperBar
                            activeStep={activeStep}
                            onStepClick={handleStickyStepClick}
                            title="Assessment Sections"
                            steps={ASSESSMENT_SECTION_STEPS}
                            startIndex={0}
                        />
                        <NurseFormStepperBar
                            activeStep={activeStep}
                            onStepClick={handleStickyStepClick}
                            title="Care Plan Sections"
                            steps={CARE_PLAN_SECTION_STEPS}
                            startIndex={10}
                        />
                    </div>
                )}

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "1rem",
                        alignItems: "start",
                    }}
                >
                    <Card
                        style={embedded
                            ? {
                                ...nursePageStyles.formCard,
                                background: "transparent",
                                border: "none",
                                boxShadow: "none",
                                padding: 0,
                            }
                            : nursePageStyles.formCard}
                    >
                        {!embedded ? (
                            <div style={{ ...nursePageStyles.formHeader, padding: mobile ? "1rem" : nursePageStyles.formHeader.padding }}>
                                <Tag value={getAssessmentStatusLabel(currentAssessmentStatus)} severity={getAssessmentStatusTagSeverity(currentAssessmentStatus)} />
                            </div>
                        ) : null}
                        <div style={embedded ? { ...nursePageStyles.formBody, padding: 0, border: "none", background: "transparent" } : nursePageStyles.formBody}>
                            {loadingForm ? (
                                <div className="flex justify-content-center" style={{ padding: "2rem" }}>
                                    <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                                </div>
                            ) : (
                                <div ref={accordionRef}>
                                    <div style={{ fontWeight: 700, color: "#0f172a", display: "block", width: "100%", margin: "0 0 0.75rem 0" }}>
                                        Expand each Assessment & Care Plan Sections (For Editing and Review)
                                    </div>

                                    <Accordion activeIndex={activeStep ?? undefined} onTabChange={(e) => setActiveStep(e.index as number | null)}>
                                        <AccordionTab header={renderAccordionHeader(0, "Resident Info", "pi pi-user", activeStep)}>
                                            <Step01ResidentInfo
                                                sectionData={step01Data ?? DEFAULT_STEP01}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep01}
                                                onNext={() => setActiveStep(1)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(1, "Evacuation", "pi pi-exclamation-triangle", activeStep)}>
                                            <Step02EmergencyEvacuation
                                                sectionData={step02Data ?? DEFAULT_STEP02}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep02}
                                                onNext={() => setActiveStep(2)}
                                                onBack={() => setActiveStep(0)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(2, "Dementia Screening", "pi pi-brain", activeStep)}>
                                            <Step03DementiaScreening
                                                sectionData={step03Data ?? DEFAULT_STEP03}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep03}
                                                onNext={() => setActiveStep(3)}
                                                onBack={() => setActiveStep(1)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(3, "Medical Diagnoses", "pi pi-heart", activeStep)}>
                                            <Step04CurrentMedicalDiagnosis
                                                sectionData={step04Data ?? DEFAULT_STEP04}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep04}
                                                onNext={() => setActiveStep(4)}
                                                onBack={() => setActiveStep(2)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(4, "Medications", "pi pi-file-edit", activeStep)}>
                                            <Step05CurrentlyPrescribedMedications
                                                sectionData={step05Data ?? DEFAULT_STEP05}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep05}
                                                onNext={() => setActiveStep(5)}
                                                onBack={() => setActiveStep(3)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(5, "Psychosocial", "pi pi-users", activeStep)}>
                                            <Step06PsychosocialProfile
                                                sectionData={step06Data ?? DEFAULT_STEP06}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep06}
                                                onNext={() => setActiveStep(6)}
                                                onBack={() => setActiveStep(4)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(6, "Mental Health", "pi pi-heart-fill", activeStep)}>
                                            <Step07MentalHealthScreening
                                                sectionData={step07Data ?? DEFAULT_STEP07}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep07}
                                                onNext={() => setActiveStep(7)}
                                                onBack={() => setActiveStep(5)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(7, "Cognition", "pi pi-lightbulb", activeStep)}>
                                            <Step08CognitiveAssessment
                                                sectionData={step08Data ?? DEFAULT_STEP08}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep08}
                                                onNext={() => setActiveStep(8)}
                                                onBack={() => setActiveStep(6)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(8, "Sensory", "pi pi-eye", activeStep)}>
                                            <Step09SensoryCommunication
                                                sectionData={step09Data ?? DEFAULT_STEP09}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep09}
                                                onNext={() => setActiveStep(9)}
                                                onBack={() => setActiveStep(7)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(9, "Medical Review", "pi pi-list-check", activeStep)}>
                                            <Step10MedicalSystemsReview
                                                sectionData={step10Data ?? DEFAULT_STEP10}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep10}
                                                onNext={() => setActiveStep(10)}
                                                onBack={() => setActiveStep(8)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(10, "Care Plan Page", "pi pi-clipboard", activeStep)}>
                                            <StepCarePlanIntro
                                                onNext={() => setActiveStep(11)}
                                                onBack={() => setActiveStep(9)}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(11, "Body Chart", "pi pi-map", activeStep)}>
                                            <BodyChartSection
                                                sectionData={step12Data ?? DEFAULT_BODY_CHART_SECTION}
                                                submitButtonClick={handleSaveStep12}
                                                nextButtonClick={() => setActiveStep(12)}
                                                backButtonClick={() => setActiveStep(10)}
                                                loadingCarePlan={savingForm}
                                                contextNote="Assessment - Body Chart will be included in the Preliminary Care Plan section"
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(12, "Daily Living", "pi pi-home", activeStep)}>
                                            <Step11ActivitiesOfDailyLiving
                                                sectionData={step11Data ?? DEFAULT_STEP11}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep11}
                                                onNext={() => setActiveStep(13)}
                                                onBack={() => setActiveStep(11)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(13, "Behavior", "pi pi-flag", activeStep)}>
                                            <Step12BehavioralObservations
                                                sectionData={step13Data ?? DEFAULT_STEP12}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep13}
                                                onNext={() => setActiveStep(14)}
                                                onBack={() => setActiveStep(12)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                        <AccordionTab header={renderAccordionHeader(14, "Signatures", "pi pi-check-circle", activeStep)}>
                                            <Step13Signatures
                                                sectionData={step14Data ?? DEFAULT_STEP13}
                                                autofillData={step14AutofillData}
                                                residentId={residentId!}
                                                formId={formId}
                                                onSave={handleSaveStep14}
                                                onNext={() => setActiveStep(14)}
                                                onBack={() => setActiveStep(13)}
                                                loading={savingForm}
                                            />
                                        </AccordionTab>
                                    </Accordion>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
                    </>
                )}

                <Dialog
                    header={renderSurfaceHeader("Start New Draft", "", automationWizardStep === "choose" ? "pi pi-bolt" : "pi pi-upload")}
                    visible={showStartDraftingDialog}
                    modal
                    onHide={() => {
                        if (!uploading) {
                            setShowStartDraftingDialog(false);
                            setAutomationWizardStep("choose");
                            setAutomationShouldCreateNewVersion(false);
                            setStartDraftDialogError(null);
                        }
                    }}
                    style={mobile ? { width: "92vw" } : startDraftDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={startDraftDialogContentStyle}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        {startDraftDialogError ? (
                            <Message
                                severity="error"
                                text={`${startDraftDialogError.summary}: ${startDraftDialogError.detail}`}
                            />
                        ) : null}

                        {automationWizardStep === "choose" ? (
                            <>
                                <div style={{ ...appMutedTextStyle, maxWidth: "36rem" }}>
                                    Choose the easiest way to start this resident&apos;s next set of care documents.
                                </div>
                                <div className="grid">
                                    <div className="col-12">
                                        <div style={{ ...appSectionCardStyle, border: "1px solid #dbe7f3", borderRadius: 18, padding: "1rem", height: "100%", background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)" }}>
                                            <div style={{ fontWeight: 800, color: "#0f172a" }}>Use Source Files</div>
                                            <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                                Upload source files like MAR sheets, past assessments, and clinical records, label them, and let AFH Hub prepare a fresh draft assessment and care plan.
                                            </div>
                                            <Button
                                                type="button"
                                                label="Continue With Files"
                                                icon="pi pi-arrow-right"
                                                severity="warning"
                                                rounded
                                                className="mt-3"
                                                onClick={() => setAutomationWizardStep("files")}
                                                disabled={uploading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ maxWidth: "36rem" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Build From Source Files</div>
                                    <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                        Add up to {MAX_STAGED_FILES} source files, such as MAR sheets, assessments, and clinical records. Label them clearly, then prepare the next draft from one setup.
                                    </div>
                                    <div
                                        className="mt-3"
                                        style={{
                                            border: "1px solid #fdba74",
                                            background: "#fff7ed",
                                            borderRadius: 16,
                                            padding: "0.9rem 1rem",
                                            boxShadow: "0 10px 24px rgba(249, 115, 22, 0.08)",
                                        }}
                                    >
                                        <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            Important
                                        </div>
                                        <div className="mt-1" style={{ color: "#7c2d12", fontWeight: 700, lineHeight: 1.65 }}>
                                            Make sure all source documents are ready before you start. Each draft job creates a new version, so it is best to upload the complete set in one run.
                                        </div>
                                    </div>
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    hidden
                                    accept={ALLOWED_ACCEPT}
                                    onChange={(event) => {
                                        setStartDraftDialogError(null);
                                        handleFilePick(event);
                                    }}
                                />

                                <div className="flex flex-wrap align-items-center justify-content-between gap-3" style={{
                                    ...appFormSectionStyle,
                                    margin: 0,
                                    padding: "0.9rem 1rem",
                                    borderRadius: 18,
                                    background: "#f8fafc",
                                    boxShadow: "none",
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Source Files</div>
                                        <div className="mt-1" style={appMutedTextStyle}>
                                            {stagedFiles.length === 0
                                                ? "Add source files, then label each one before preparing the draft."
                                                : `${stagedFiles.length} of ${MAX_STAGED_FILES} files ready for review.`}
                                        </div>
                                        {hasActiveAssessmentDraftJob ? (
                                            <div className="mt-2" style={{ color: "#9a3412", fontWeight: 700, fontSize: "0.84rem" }}>
                                                An assessment draft job is already running for this resident.
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <Button
                                            type="button"
                                            label={`Add Files (${stagedFiles.length}/${MAX_STAGED_FILES})`}
                                            icon="pi pi-upload"
                                            severity="secondary"
                                            rounded
                                            outlined
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading || stagedFiles.length >= MAX_STAGED_FILES}
                                        />
                                        <Button
                                            type="button"
                                            label="Prepare Draft"
                                            icon="pi pi-bolt"
                                            severity="warning"
                                            rounded
                                            onClick={() => { void handleUploadAll(); }}
                                            disabled={uploading || stagedFiles.length === 0 || hasActiveAssessmentDraftJob}
                                        />
                                        <Button
                                            type="button"
                                            label="Back"
                                            icon="pi pi-arrow-left"
                                            severity="secondary"
                                            rounded
                                            outlined
                                            onClick={() => setAutomationWizardStep("choose")}
                                            disabled={uploading}
                                        />
                                    </div>
                                </div>

                                {uploading ? (
                                    <div
                                        className="flex flex-column align-items-center justify-content-center"
                                        style={{
                                            ...appFormSectionStyle,
                                            margin: 0,
                                            borderRadius: 18,
                                            background: "#fffaf0",
                                            boxShadow: "none",
                                            padding: "1rem",
                                        }}
                                    >
                                        <ProgressSpinner style={{ width: "42px", height: "42px" }} />
                                        <div style={{ marginTop: "0.75rem", color: "#475569", fontWeight: 700 }}>
                                            Uploading source files and preparing draft...
                                        </div>
                                        <div style={{ marginTop: "0.35rem", color: "#64748b", textAlign: "center" }}>
                                            Please keep this dialog open while AFH Hub uploads your files and starts the draft job.
                                        </div>
                                        <div
                                            className="mt-3"
                                            style={{
                                                width: "100%",
                                                maxWidth: "28rem",
                                                border: "1px solid #fde68a",
                                                borderRadius: 14,
                                                background: "#ffffff",
                                                padding: "0.85rem 1rem",
                                            }}
                                        >
                                            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                                Upload Documents
                                            </div>
                                            <div style={{ marginTop: "0.35rem", fontWeight: 700, color: "#0f172a" }}>
                                                In progress
                                            </div>
                                            <div style={{ marginTop: "0.2rem", color: "#64748b", fontSize: "0.9rem" }}>
                                                Uploading {stagedFiles.length} file{stagedFiles.length === 1 ? "" : "s"} before draft processing begins.
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {stagedFiles.length > 0 ? (
                                    <div className="flex flex-column gap-3">
                                        {stagedFiles.map((staged) => (
                                            <div
                                                key={staged.id}
                                                style={{
                                                    ...appFormSectionStyle,
                                                    margin: 0,
                                                    border: tagValidationRequested && !(staged.tags?.length) ? "1px solid #ef4444" : "1px solid #dbe7f3",
                                                    borderRadius: 18,
                                                    background: tagValidationRequested && !(staged.tags?.length) ? "#fef2f2" : "#ffffff",
                                                    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
                                                }}
                                            >
                                                <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{staged.file.name}</div>
                                                        <div className="mt-1" style={appMutedTextStyle}>
                                                            {(staged.file.size / (1024 * 1024)).toFixed(1)} MB
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        icon="pi pi-times"
                                                        text
                                                        rounded
                                                        severity="secondary"
                                                        onClick={() => removeStagedFile(staged.id)}
                                                    />
                                                </div>
                                                <div className="mt-3">
                                                    <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.55rem" }}>
                                                        File Labels
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {DOC_TAG_OPTIONS.map((option) => {
                                                            const isSelected = (staged.tags || []).includes(option.value);
                                                            return (
                                                                <Button
                                                                    key={`${staged.id}-${option.value}`}
                                                                    type="button"
                                                                    label={option.label}
                                                                    size="small"
                                                                    rounded
                                                                    severity={isSelected ? "warning" : "secondary"}
                                                                    outlined={!isSelected}
                                                                    onClick={() => toggleStagedTag(staged.id, option.value)}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                    {tagValidationRequested && !(staged.tags?.length) ? (
                                                        <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.82rem", fontWeight: 600 }}>
                                                            Choose at least one file label to continue.
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={appMutedTextStyle}>
                                        Add up to {MAX_STAGED_FILES} files, review that the set is complete, then choose a label for each file before preparing the draft.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Previous Assessment Versions", "", "pi pi-history")}
                    visible={showAssessmentHistoryDialog}
                    modal
                    onHide={() => setShowAssessmentHistoryDialog(false)}
                    style={mobile ? { width: "92vw" } : appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {assessmentHeaders.length === 0 ? (
                        <div style={appMutedTextStyle}>No saved assessment versions found yet.</div>
                    ) : (
                        <div className="flex flex-column gap-2">
                            {assessmentHeaders.map((assessment: any, index: number) => {
                                const normalizedFormId = String(assessment?.formId || "").trim();
                                const isCurrent = normalizedFormId === String(formId || "").trim();
                                return (
                                    <div key={normalizedFormId || index} style={{ ...appSectionCardStyle, padding: "0.95rem", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                                        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                            <div>
                                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                                    {assessment?.version ? `Version V${assessment.version}` : "Saved assessment version"}
                                                </div>
                                                <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                                                    {assessment?.modifiedAt
                                                        ? `Last updated ${new Date(assessment.modifiedAt).toLocaleString()}`
                                                        : assessment?.createdAt
                                                            ? `Created ${new Date(assessment.createdAt).toLocaleString()}`
                                                            : "Saved assessment version"}
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                                                <Tag value={getAssessmentStatusLabel(assessment?.status).toUpperCase()} severity={getAssessmentStatusTagSeverity(assessment?.status)} />
                                                {isCurrent ? <Tag value="Current" severity="success" /> : null}
                                                <Button
                                                    type="button"
                                                    label={isCurrent ? "Open Current" : "Open"}
                                                    icon="pi pi-folder-open"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    size="small"
                                                    onClick={() => handleOpenAssessmentVersion(normalizedFormId)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader(
                        "Need More Access",
                        "",
                        "pi pi-wallet",
                    )}
                    visible={showFeatureAccessDialog}
                    onHide={() => {
                        setShowFeatureAccessDialog(false);
                        setUploading(false);
                    }}
                    style={mobile ? { width: "92vw" } : appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    modal
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                            {featureAccessDialogMessage}
                        </div>
                        {!requiresProviderSubscriptionMessageOnly ? (
                            <>
                                <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
                                    <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
                                    <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                        {isProviderResidentMode
                                            ? (featureAccessDialogAvailableCredits > 0
                                                ? `You currently have ${featureAccessDialogAvailableCredits} nurse automation credit${featureAccessDialogAvailableCredits === 1 ? "" : "s"} available.`
                                                : "Buy Automation Credit to keep going.")
                                            : "Open Billing to keep going."}
                                    </div>
                                </div>
                                <div className="flex flex-wrap justify-content-end gap-2">
                                    {featureAccessDialogAllowUseCredit && featureAccessDialogUseCreditAction ? (
                                        <Button
                                            type="button"
                                            label="Use 1 Credit"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={() => {
                                                setShowFeatureAccessDialog(false);
                                                setUploading(false);
                                                featureAccessDialogUseCreditAction();
                                            }}
                                        />
                                    ) : null}
                                    {(!isProviderResidentMode || featureAccessDialogAvailableCredits <= 0) ? (
                                        <Button
                                            type="button"
                                            label={isProviderResidentMode ? "Buy Now" : "Open Billing"}
                                            severity="warning"
                                            rounded
                                            onClick={() => {
                                                setShowFeatureAccessDialog(false);
                                                setUploading(false);
                                                navigate(isProviderResidentMode ? `${RouterAddress.BILLING}?tab=one-time` : RouterAddress.BILLING);
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </>
                        ) : null}
                    </div>
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Drafting Progress", "", "pi pi-sync")}
                    visible={showProcessingDialog}
                    modal
                    onHide={handleCloseProcessingDialog}
                    style={appWideDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    footer={
                        <div className="flex align-items-center justify-content-between gap-3 flex-wrap" style={{ width: "100%" }}>
                            <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic", flex: 1 }}>
                                {AI_MESSAGE_FOR_COMPLIANCE}
                            </p>
                            <Button
                                type="button"
                                label={isProcessingCompleted ? "Close and Review" : "Close"}
                                severity="secondary"
                                rounded
                                outlined
                                onClick={handleCloseProcessingDialog}
                            />
                        </div>
                    }
                >
                    {!shouldShowProcessingDialogContent ? (
                        <div style={appFormSectionStyle}>
                            <div style={appMutedTextStyle}>
                                No documents are currently queued or processing.
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-column gap-3">
                            <div style={{
                                ...appNeutralPanelStyle,
                                border: isProcessingCompleted
                                    ? "1px solid #86efac"
                                    : isProcessingFailed
                                        ? "1px solid #fdba74"
                                        : "1px solid #dbe7f3",
                                background: isProcessingCompleted
                                    ? "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)"
                                    : isProcessingFailed
                                        ? "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)"
                                        : undefined,
                                margin: 0,
                            }}>
                                <div className="flex flex-column gap-3">
                                    <div>
                                        <div className="flex align-items-center gap-3" style={{ marginBottom: "0.45rem" }}>
                                            {!isProcessingCompleted && !isProcessingFailed ? (
                                                <ProgressSpinner
                                                    style={{ width: 34, height: 34, flexShrink: 0 }}
                                                    strokeWidth="4"
                                                    animationDuration=".8s"
                                                />
                                            ) : null}
                                            <div style={{
                                                fontWeight: 800,
                                                color: isProcessingCompleted
                                                    ? "#166534"
                                                    : isProcessingFailed
                                                        ? "#9a3412"
                                                        : "#0f172a",
                                            }}>
                                                {isProcessingCompleted
                                                    ? "Draft is ready"
                                                    : isProcessingFailed
                                                        ? "Draft preparation finished with issues"
                                                        : "Draft is being prepared"}
                                            </div>
                                        </div>
                                        <div style={appMutedTextStyle}>
                                            {isProcessingCompleted
                                                ? "You can close this now and review the new draft."
                                                : isProcessingFailed
                                                    ? "You can try again or continue by hand."
                                                    : `Current status: ${formatProcessingStatus(processingJobDetails?.jobSubStatus || processingJobDetails?.jobStatus)}${currentSectionName ? ` • ${currentSectionName}` : ""}`}
                                        </div>
                                    </div>
                                    <div className="grid" style={{ margin: 0 }}>
                                        {processingSummaryItems.map((item) => (
                                            <div key={item.label} className="col-12 md:col-4" style={{ paddingTop: "0.35rem", paddingBottom: 0 }}>
                                                <div style={{ ...appFormSectionStyle, margin: 0, padding: "0.85rem 0.95rem", borderRadius: 16, boxShadow: "none" }}>
                                                    <div style={dialogMetricLabelStyle}>{item.label}</div>
                                                    <div style={{ fontWeight: 700, color: "#0f172a", marginTop: "0.35rem" }}>
                                                        {item.value}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {shouldShowUploadProgressSection ? null : null}
                            {(visibleProgressSections.length > 0 || pendingSections.length > 0) ? (
                                <div style={appFormSectionStyle}>
                                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Assessment Sections</div>
                                    {visibleProgressSections.length > 0 ? (
                                        <div style={{ marginBottom: pendingSections.length > 0 ? "0.85rem" : 0 }}>
                                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#166534", marginBottom: "0.45rem" }}>
                                                Completed
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {visibleProgressSections.map((section: any, index: number) => (
                                                    <span
                                                        key={`${section?.recordType || section?.description || "completed"}-${index}`}
                                                        style={getStatusChipStyle("success")}
                                                    >
                                                        {getSectionDisplayName(section)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    {pendingSections.length > 0 ? (
                                        <div>
                                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#92400e", marginBottom: "0.45rem" }}>
                                                Pending
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {pendingSections.map((section: any, index: number) => (
                                                    <span
                                                        key={`${section?.recordType || section?.description || "pending"}-${index}`}
                                                        style={getStatusChipStyle("warning")}
                                                    >
                                                        {getSectionDisplayName(section)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {delegationDraftGenerationRequested ? (
                                <div style={appFormSectionStyle}>
                                    <div className="flex align-items-center gap-2" style={{ marginBottom: "0.75rem" }}>
                                        {isDelegationDraftGenerationInProgress ? (
                                            <ProgressSpinner
                                                style={{ width: 22, height: 22, flexShrink: 0 }}
                                                strokeWidth="5"
                                                animationDuration=".8s"
                                            />
                                        ) : null}
                                        <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                            Delegation Drafts
                                        </div>
                                    </div>
                                    {generatedDelegationForms.length > 0 ? (
                                        <>
                                            <div style={{ ...appMutedTextStyle, marginBottom: "0.75rem" }}>
                                                New draft delegation forms were created from this guided draft.
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {generatedDelegationForms.map((form: any, index: number) => (
                                                    <span
                                                        key={`${form?.formId || form?.formCode || "delegation"}-${index}`}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: 999,
                                                            padding: "0.25rem 0.65rem",
                                                            fontSize: "0.78rem",
                                                            fontWeight: 700,
                                                            background: "#eff6ff",
                                                            color: "#1d4ed8",
                                                            border: "1px solid #bfdbfe",
                                                        }}
                                                    >
                                                        {String(form?.formTitle || form?.formCode || "Delegation Form").trim()}
                                                        {form?.version ? ` • v${form.version}` : ""}
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={appMutedTextStyle}>
                                            {isDelegationDraftGenerationInProgress
                                                ? "Delegation draft forms are being generated now."
                                                : delegationDraftGenerationStatus === "failed"
                                                    ? "The draft finished, but the delegation forms were not created."
                                                    : delegationDraftGenerationStatus === "completed"
                                                        ? "No delegation draft forms were created from this run."
                                                        : "Delegation draft forms will be prepared after the assessment is completed successfully."}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    )}
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Assessment Files", "", "pi pi-folder-open")}
                    visible={showAssessmentDocsDialog}
                    modal
                    onHide={() => setShowAssessmentDocsDialog(false)}
                    style={appWideDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {existingDocuments.length === 0 ? (
                        <div style={appFormSectionStyle}>
                            <div style={appMutedTextStyle}>
                                No completed assessment documents are available yet.
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: "0.9rem",
                                alignItems: "stretch",
                            }}
                        >
                            {existingDocuments.map((doc: any) => renderDocumentRow(doc, false))}
                        </div>
                    )}
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Document Viewer", "", "pi pi-eye")}
                    visible={showDocViewer}
                    modal
                    onHide={() => {
                        setShowDocViewer(false);
                        setDocViewerImageUrl("");
                        setDocViewerNonImageUrl("");
                    }}
                    style={appWideDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {docViewerNonImageUrl && (
                        <div style={appFormSectionStyle}>
                            <iframe
                                src={docViewerNonImageUrl}
                                style={{ width: "100%", height: "500px", objectFit: "contain", border: "1px solid #219d51" }}
                                title="Document Preview Viewer"
                            ></iframe>
                        </div>
                    )}
                    {docViewerImageUrl && (
                        <div style={appFormSectionStyle}>
                            <img
                                src={docViewerImageUrl}
                                style={{ width: "100%", height: "500px", objectFit: "contain", border: "1px solid #219d51" }}
                                title="Document Preview Viewer"
                            />
                        </div>
                    )}
                </Dialog>
            </div>
        </div>
    );
};

export default NurseResidentAssessmentPage;


